import { Card, Typography, Box, IconButton, TextField, Snackbar, Alert, Popover, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import ArgonButton from "components/ArgonButton";
import LoadingSpinner from "components/LoadingSpinner";
import SkeletonLoader from "components/SkeletonLoader";
import React, { useCallback, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import { useUser } from "context/UserContext";
import {
  NotificationsOutlined,
  Schedule,
  CalendarToday,
  Add,
  Close,
  Loop,
  Refresh,
  Delete,
  ChevronLeft,
  ChevronRight
} from "@mui/icons-material";

const Horarios = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, getToken } = useAuth();
  useUser();
  const [timeSlots, setTimeSlots] = useState([]);
  const [originalTimeSlots, setOriginalTimeSlots] = useState([]); // Para comparar cambios
  const [isLoading, setIsLoading] = useState(true);
  const [imageLoading, setImageLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [showOverlapMessage, setShowOverlapMessage] = useState(false);
  const [hasOverlapError, setHasOverlapError] = useState(false);
  const [modalOverlapError, setModalOverlapError] = useState(false);
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState(null);
  const saveTimeoutRef = useRef(null);

  // Referencias para los inputs de tiempo
  const timeInputRefs = useRef({});

  // Estado para los valores de texto de los inputs de tiempo
  const [timeInputValues, setTimeInputValues] = useState({});

  // Función para validar formato de hora (HH:mm)
  const validateTimeFormat = (timeString) => {
    if (!timeString) return false;
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    return timeRegex.test(timeString);
  };

  // Función para convertir string de hora a Date
  const timeStringToDate = useCallback((timeString) => {
    if (!timeString || !validateTimeFormat(timeString)) return null;
    const [hours, minutes] = timeString.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  }, []);

  // Función para convertir Date a string de hora
  const dateToTimeString = (date) => {
    if (!date || !(date instanceof Date)) return '';
    return date.toTimeString().slice(0, 5);
  };

  // Función para obtener el valor de texto del input
  const getTimeInputValue = (slotId, field) => {
    const key = `${slotId}-${field}`;
    // Si hay un valor en timeInputValues, usarlo (incluso si está vacío)
    if (timeInputValues.hasOwnProperty(key)) {
      return timeInputValues[key];
    }
    // Solo usar el valor por defecto si no hay ningún valor en timeInputValues
    return dateToTimeString(timeSlots.find(slot => slot.id === slotId)?.[field]) || '';
  };

  // Función para formatear la entrada de tiempo
  const formatTimeInput = (value, previousValue = '') => {
    // Remover caracteres no numéricos excepto ':'
    let formatted = value.replace(/[^\d:]/g, '');

    // Detectar si se está borrando contenido
    const isDeleting = value.length < previousValue.length;

    // Si hay más de un ':', mantener solo el primero
    const colonIndex = formatted.indexOf(':');
    if (colonIndex !== -1) {
      formatted = formatted.slice(0, colonIndex + 1) + formatted.slice(colonIndex + 1).replace(/:/g, '');
    }

    // Limitar dígitos antes de ':'
    if (colonIndex !== -1) {
      const beforeColon = formatted.slice(0, colonIndex);
      const afterColon = formatted.slice(colonIndex + 1);

      // Máximo 2 dígitos antes de ':'
      const limitedBeforeColon = beforeColon.slice(0, 2);

      // Máximo 2 dígitos después de ':'
      const limitedAfterColon = afterColon.slice(0, 2);

      // Si se está borrando y los minutos están vacíos, remover también los minutos y el ':'
      if (isDeleting && limitedAfterColon === '' && limitedBeforeColon !== '') {
        formatted = limitedBeforeColon;
      } else {
        formatted = limitedBeforeColon + ':' + limitedAfterColon;
      }
    } else {
      // Si no hay ':', agregar automáticamente después de 2 dígitos
      if (formatted.length >= 2) {
        formatted = formatted.slice(0, 2) + ':' + formatted.slice(2, 4); // Solo 2 dígitos después
      } else {
        // Limitar a máximo 2 dígitos antes de agregar ':'
        formatted = formatted.slice(0, 2);
      }
    }

    // Validar que las horas estén entre 00-23
    if (formatted.length >= 2) {
      const hours = parseInt(formatted.slice(0, 2));
      if (hours > 23) {
        formatted = '23' + formatted.slice(2);
      }
    }

    // Validar que los minutos estén entre 00-59
    if (formatted.length >= 5) {
      const minutes = parseInt(formatted.slice(3, 5));
      if (minutes > 59) {
        formatted = formatted.slice(0, 3) + '59';
      }
    }

    return formatted;
  };

  // Función para validar si un horario es válido (endTime debe ser al menos 1 hora mayor que startTime)
  const isValidTimeSlot = (slot) => {
    if (!slot.startTime || !slot.endTime) return true; // Permitir slots incompletos

    // Calcular la diferencia en milisegundos
    const timeDiff = slot.endTime.getTime() - slot.startTime.getTime();
    // 1 hora = 60 minutos = 3600 segundos = 3,600,000 milisegundos
    const oneHourInMs = 60 * 60 * 1000;

    return timeDiff >= oneHourInMs;
  };

  // Función para validar si un rango de tiempo es válido (endTime debe ser al menos 1 hora mayor que startTime)
  const isValidTimeRange = (startTime, endTime) => {
    if (!startTime || !endTime) return false;

    // Calcular la diferencia en milisegundos
    const timeDiff = endTime.getTime() - startTime.getTime();
    // 1 hora = 60 minutos = 3600 segundos = 3,600,000 milisegundos
    const oneHourInMs = 60 * 60 * 1000;

    return timeDiff >= oneHourInMs;
  };

  // Función para detectar si hay solapamiento entre horarios del mismo día
  const hasTimeSlotOverlap = (slotToCheck, allSlots) => {
    // Solo puede detectar solapamiento si el horario está completo (ambos tiempos definidos)
    if (!slotToCheck.startTime || !slotToCheck.endTime) return false;

    const startTime = slotToCheck.startTime.getTime();
    const endTime = slotToCheck.endTime.getTime();

    // Buscar otros slots del mismo día que tengan horarios completamente definidos
    const sameDaySlots = allSlots.filter(slot =>
      slot.day === slotToCheck.day &&
      slot.id !== slotToCheck.id &&
      slot.startTime &&
      slot.endTime
    );

    // Verificar si hay solapamiento con alguno de los otros slots del mismo día
    return sameDaySlots.some(slot => {
      const existingStart = slot.startTime.getTime();
      const existingEnd = slot.endTime.getTime();

      // Dos intervalos se solapan si: start1 < end2 && start2 < end1
      return startTime < existingEnd && existingStart < endTime;
    });
  };

  // Función para manejar el cambio de hora y navegación automática
  const handleTimeInputChange = (slotId, field, value) => {
    // Obtener el valor anterior para detectar si se está borrando
    const key = `${slotId}-${field}`;
    const previousValue = timeInputValues[key] || '';

    // Formatear la entrada
    const formattedValue = formatTimeInput(value, previousValue);

    // Actualizar el valor de texto inmediatamente
    setTimeInputValues(prev => ({
      ...prev,
      [key]: formattedValue
    }));

    // Si el campo está vacío, limpiar el valor en timeSlots
    if (formattedValue === '') {
      const newTimeSlots = timeSlots.map(slot => {
        if (slot.id === slotId) {
          return { ...slot, [field]: null };
        }
        return slot;
      });
      setTimeSlots(newTimeSlots);
      // Guardar automáticamente después del cambio
      debouncedSaveWithState(newTimeSlots);
      return;
    }

    // Solo actualizar el estado de timeSlots si el formato es válido
    if (validateTimeFormat(formattedValue)) {
      const timeDate = new Date(`2000-01-01T${formattedValue}:00`);
      const newTimeSlots = timeSlots.map(slot => {
        if (slot.id === slotId) {
          return { ...slot, [field]: timeDate };
        }
        return slot;
      });

      // Obtener el slot actualizado para las validaciones
      const updatedSlot = newTimeSlots.find(slot => slot.id === slotId);

      // Primero verificar si hay solapamiento (esta validación tiene prioridad)
      if (hasTimeSlotOverlap(updatedSlot, newTimeSlots)) {
        // Si hay solapamiento, mostrar mensaje específico y revertir
        setShowOverlapMessage(true);
        setTimeInputValues(prev => ({
          ...prev,
          [key]: getTimeInputValue(slotId, field)
        }));
        return;
      }

      // Luego verificar la duración válida
      if (isValidTimeSlot(updatedSlot)) {
        setTimeSlots(newTimeSlots);

        // Guardar automáticamente después del cambio
        debouncedSaveWithState(newTimeSlots);
      } else {
        // Si el horario es inválido por duración, mostrar mensaje de error solo si NO estamos en el modal de fechas específicas
        if (!showDateModal) {
          setShowErrorMessage(true);
        }
        // Revertir el cambio en el valor de texto
        setTimeInputValues(prev => ({
          ...prev,
          [key]: getTimeInputValue(slotId, field)
        }));
      }
    }
  };

  // Función para manejar el evento de tecla presionada
  const handleTimeInputKeyDown = (slotId, field, event) => {
    if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault();

      // Si es startTime, navegar a endTime
      if (field === 'startTime') {
        const endTimeInputId = `end-${slotId}`;
        const endTimeInput = timeInputRefs.current[endTimeInputId];
        if (endTimeInput) {
          endTimeInput.focus();
          endTimeInput.select();
        }
      }
    }

    // Manejar el borrado especial para cuando se borran los minutos (aplica tanto para startTime como endTime)
    if (event.key === 'Backspace' || event.key === 'Delete') {
      const input = event.target;
      const value = input.value;
      const selectionStart = input.selectionStart;
      const selectionEnd = input.selectionEnd;

      // Si estamos borrando y el campo tiene formato "HH:" (horas con : pero sin minutos)
      if (value.length === 3 && value.endsWith(':') && selectionStart === 3 && selectionEnd === 3 && event.key === 'Backspace') {
        event.preventDefault();
        // Remover el : y dejar solo las horas
        const newValue = value.slice(0, 2);
        input.value = newValue;
        // Actualizar el estado
        const key = `${slotId}-${field}`;
        setTimeInputValues(prev => ({
          ...prev,
          [key]: newValue
        }));
        // Posicionar el cursor al final
        setTimeout(() => {
          input.setSelectionRange(newValue.length, newValue.length);
        }, 0);
      }

      // Si estamos borrando y el campo está completamente vacío, permitir que se mantenga vacío
      if (value === '' && event.key === 'Backspace') {
        // No hacer nada especial, permitir que el campo se mantenga vacío
        return;
      }
    }
  };

  // Estados para fechas específicas
  const [specificDates, setSpecificDates] = useState([]);
  const [showDateModal, setShowDateModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedStartTime, setSelectedStartTime] = useState(null);
  const [selectedEndTime, setSelectedEndTime] = useState(null);

  // Estados para los valores de texto del modal
  const [, setModalStartTimeText] = useState('');
  const [, setModalEndTimeText] = useState('');
  // Estados para el nuevo modal con calendario y múltiples rangos
  const [calendarYear, setCalendarYear] = useState(() => {
    const now = new Date();
    return now.getFullYear();
  });
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const now = new Date();
    return now.getMonth(); // 0-11
  });
  const [selectedDateKey, setSelectedDateKey] = useState(null); // 'YYYY-MM-DD' o null
  const [modalTimeRanges, setModalTimeRanges] = useState([{ id: 1, startText: '', endText: '' }]);
  const [showConfirmSaveBulk, setShowConfirmSaveBulk] = useState(false);

  // Estados para bloquear horario
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [blockSelectedDay, setBlockSelectedDay] = useState(null); // 'L', 'M', 'Mi', 'J', 'V', 'S', 'D' o null
  const [blockStartTime, setBlockStartTime] = useState('');
  const [blockEndTime, setBlockEndTime] = useState('');
  const [isBlocking, setIsBlocking] = useState(false);
  const [blockedSchedules, setBlockedSchedules] = useState([]); // Array de horarios bloqueados: [{ id, profesionalId, diaSemana, hora }]

  // Helpers calendario
  const getMonthMatrix = (year, month) => {
    const firstDay = new Date(year, month, 1);
    const startWeekDay = (firstDay.getDay() + 6) % 7; // Lunes=0
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const matrix = [];
    let dayCounter = 1 - startWeekDay;
    for (let r = 0; r < 6; r++) { // Siempre 6 filas
      const row = [];
      for (let c = 0; c < 7; c++) {
        if (dayCounter < 1 || dayCounter > daysInMonth) {
          row.push(null);
        } else {
          // Crear fecha de forma segura sin problemas de zona horaria
          const date = new Date(year, month, dayCounter);
          row.push(date);
        }
        dayCounter++;
      }
      matrix.push(row);
    }
    return matrix;
  };

  const formatDateKey = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const toggleSelectDate = (date) => {
    if (!date) return;
    const key = formatDateKey(date);
    setSelectedDateKey(prev => prev === key ? null : key);
  };

  const goPrevMonth = () => {
    const d = new Date(calendarYear, calendarMonth, 1);
    d.setMonth(d.getMonth() - 1);
    setCalendarYear(d.getFullYear());
    setCalendarMonth(d.getMonth());
  };

  const goNextMonth = () => {
    const d = new Date(calendarYear, calendarMonth, 1);
    d.setMonth(d.getMonth() + 1);
    setCalendarYear(d.getFullYear());
    setCalendarMonth(d.getMonth());
  };

  const toggleSelectDayBlock = (dayLetter) => {
    setBlockSelectedDay(prev => prev === dayLetter ? null : dayLetter);
  };

  // Función para obtener bloqueos
  const fetchBloqueos = async () => {
    try {
      const token = await getToken();
      if (!token) {
        navigate("/authentication/sign-in");
        return;
      }

      const profesionalId = localStorage.getItem('id');
      const baseURL = process.env.REACT_APP_BASE_URL;

      if (!profesionalId) {
        console.log('No hay profesionalId para obtener bloqueos');
        return [];
      }

      console.log('Obteniendo bloqueos para profesional:', profesionalId);
      const url = `${baseURL}/bloqueos-profesional/${profesionalId}`;
      console.log('URL de bloqueos:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Respuesta de bloqueos:', response.status, response.statusText);

      if (response.ok) {
        const data = await response.json();
        console.log('Bloqueos obtenidos:', data);
        // Asegurarse de que data es un array
        const bloqueosArray = Array.isArray(data) ? data : [];
        console.log('Bloqueos procesados (array):', bloqueosArray);
        setBlockedSchedules(bloqueosArray);
        return bloqueosArray;
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error al obtener bloqueos:', response.status, errorData);
        return [];
      }
    } catch (error) {
      console.error('Error al obtener bloqueos:', error);
      return [];
    }
  };

  // Función para eliminar un bloqueo
  const deleteBloqueo = async (bloqueoId) => {
    try {
      const token = await getToken();
      if (!token) {
        navigate("/authentication/sign-in");
        return false;
      }

      const baseURL = process.env.REACT_APP_BASE_URL;
      const response = await fetch(`${baseURL}/bloqueos/${bloqueoId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        // Recargar bloqueos
        await fetchBloqueos();
        setSuccessMessage('Bloqueo eliminado exitosamente');
        setShowSuccessMessage(true);
        return true;
      } else {
        console.error('Error al eliminar bloqueo');
        setShowErrorMessage(true);
        return false;
      }
    } catch (error) {
      console.error('Error al eliminar bloqueo:', error);
      setShowErrorMessage(true);
      return false;
    }
  };

  // Función para convertir día de la semana (L, M, Mi, J, V, S, D) a número (1-7)
  const dayLetterToNumber = (dayLetter) => {
    const diasSemana = { "L": 1, "M": 2, "Mi": 3, "J": 4, "V": 5, "S": 6, "D": 7 };
    return diasSemana[dayLetter] || null;
  };

  // Función para validar que existe un horario disponible que cubra el rango a bloquear
  const validateBlockRange = (dayLetter, horaInicio, horaFin) => {
    // Buscar en horarios recurrentes
    const slotsDelDia = timeSlots.filter(slot => slot.day === dayLetter && slot.startTime && slot.endTime);
    
    // Convertir horas a minutos para comparar
    const [hInicio, mInicio] = horaInicio.split(':').map(Number);
    const [hFin, mFin] = horaFin.split(':').map(Number);
    const minutosInicio = hInicio * 60 + mInicio;
    const minutosFin = hFin * 60 + mFin;

    // Verificar si algún slot cubre el rango a bloquear
    for (const slot of slotsDelDia) {
      const slotStart = slot.startTime.getHours() * 60 + slot.startTime.getMinutes();
      const slotEnd = slot.endTime.getHours() * 60 + slot.endTime.getMinutes();
      
      // El slot debe cubrir completamente el rango a bloquear
      if (slotStart <= minutosInicio && slotEnd >= minutosFin) {
        return true;
      }
    }

    return false;
  };

  // Función para bloquear un horario
  const blockSchedule = async () => {
    if (!blockSelectedDay || !blockStartTime || !blockEndTime) {
      setShowErrorMessage(true);
      return;
    }

    // Validar formato de horas
    if (!validateTimeFormat(blockStartTime) || !validateTimeFormat(blockEndTime)) {
      setShowErrorMessage(true);
      return;
    }

    // Convertir día de la semana a número
    const diaSemana = dayLetterToNumber(blockSelectedDay);
    if (!diaSemana) {
      setShowErrorMessage(true);
      return;
    }

    // Validar que existe un horario disponible que cubra el rango a bloquear
    if (!validateBlockRange(blockSelectedDay, blockStartTime, blockEndTime)) {
      setSuccessMessage('No existe un horario disponible que cubra el rango seleccionado');
      setShowErrorMessage(true);
      return;
    }

    setIsBlocking(true);

    try {
      const token = await getToken();
      if (!token) {
        navigate("/authentication/sign-in");
        return;
      }

      const profesionalId = localStorage.getItem('id');
      const baseURL = process.env.REACT_APP_BASE_URL;

      if (!profesionalId) {
        setShowErrorMessage(true);
        setIsBlocking(false);
        return;
      }

      const response = await fetch(`${baseURL}/bloquear-horario`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          profesionalId: parseInt(profesionalId),
          diaSemana: diaSemana,
          horaInicio: blockStartTime,
          horaFin: blockEndTime
        }),
      });

      if (response.ok) {
        const responseData = await response.json();
        const bloqueados = responseData.resultados || [];
        
        setSuccessMessage(`Horario bloqueado exitosamente. ${responseData.bloqueados || bloqueados.length} bloqueo(s) creado(s)`);
        setShowSuccessMessage(true);
        
        // Recargar bloqueos
        await fetchBloqueos();
        
        // Recargar disponibilidades para reflejar el fraccionamiento
        // El backend debería devolver los horarios fraccionados con isBlocked: true
        const loadDisponibilidades = async () => {
          setIsLoading(true);
          try {
            const disponibilidades = await fetchDisponibilidadesRecurrentes();
            // Recargar los slots con los datos actualizados del backend
            // El backend debería haber fraccionado los horarios y marcado los bloqueados
            if (disponibilidades && disponibilidades.length > 0) {
              // Procesar disponibilidades como se hace en el useEffect inicial
              const allDaysTimeSlots = [...defaultTimeSlots];
              const recurrentes = disponibilidades.filter(d => d.diaSemana);
              
              if (recurrentes.length > 0) {
                const diasSemana = {
                  1: "L", 2: "M", 3: "Mi", 4: "J", 5: "V", 6: "S", 7: "D"
                };
                
                const disponibilidadesPorDia = {};
                recurrentes.forEach((disp) => {
                  const dayLetter = diasSemana[disp.diaSemana];
                  if (dayLetter) {
                    if (!disponibilidadesPorDia[dayLetter]) {
                      disponibilidadesPorDia[dayLetter] = [];
                    }
                    disponibilidadesPorDia[dayLetter].push(disp);
                  }
                });
                
                Object.keys(disponibilidadesPorDia).forEach(dayLetter => {
                  const slotsDelDia = disponibilidadesPorDia[dayLetter];
                  const nuevosSlots = [];
                  slotsDelDia.forEach((disp, index) => {
                    const slotId = index === 0 ?
                      allDaysTimeSlots.find(slot => slot.day === dayLetter)?.id ||
                      Math.max(...allDaysTimeSlots.map(slot => slot.id)) + index + 1 :
                      Math.max(...allDaysTimeSlots.map(slot => slot.id)) + index + 1;
                    
                    nuevosSlots.push({
                      id: slotId,
                      startTime: disp.horaInicio ? new Date(`2000-01-01T${disp.horaInicio}`) : null,
                      endTime: disp.horaFin ? new Date(`2000-01-01T${disp.horaFin}`) : null,
                      day: dayLetter,
                      isAdditional: index > 0,
                      isBlocked: disp.bloqueado || false // El backend debería marcar los bloqueados
                    });
                  });
                  
                  const dayIndex = allDaysTimeSlots.findIndex(slot => slot.day === dayLetter);
                  if (dayIndex !== -1) {
                    allDaysTimeSlots[dayIndex] = nuevosSlots[0];
                    nuevosSlots.slice(1).forEach((slot, index) => {
                      allDaysTimeSlots.splice(dayIndex + 1 + index, 0, slot);
                    });
                  }
                });
              }
              
              setTimeSlots(allDaysTimeSlots);
            }
          } catch (error) {
            console.error('Error al recargar disponibilidades:', error);
          } finally {
            setIsLoading(false);
          }
        };
        
        loadDisponibilidades();
        handleCloseBlockModal();
      } else {
        const errorData = await response.json();
        console.error('Error al bloquear horario:', errorData);
        setShowErrorMessage(true);
      }
    } catch (error) {
      console.error('Error al bloquear horario:', error);
      setShowErrorMessage(true);
    } finally {
      setIsBlocking(false);
    }
  };

  // Función para abrir el modal de bloquear horario
  const handleOpenBlockModal = () => {
    setShowBlockModal(true);
    // Resetear estados
    setBlockSelectedDay(null);
    setBlockStartTime('');
    setBlockEndTime('');
  };

  // Función para cerrar el modal de bloquear horario
  const handleCloseBlockModal = () => {
    setShowBlockModal(false);
    setBlockSelectedDay(null);
    setBlockStartTime('');
    setBlockEndTime('');
  };

  // Valores por defecto cuando no se obtengan disponibilidades recurrentes
  const defaultTimeSlots = [
    { id: 1, startTime: null, endTime: null, day: "L", isAdditional: false },
    { id: 2, startTime: null, endTime: null, day: "M", isAdditional: false },
    { id: 3, startTime: null, endTime: null, day: "Mi", isAdditional: false },
    { id: 4, startTime: null, endTime: null, day: "J", isAdditional: false },
    { id: 5, startTime: null, endTime: null, day: "V", isAdditional: false },
    { id: 6, startTime: null, endTime: null, day: "S", isAdditional: false },
    { id: 7, startTime: null, endTime: null, day: "D", isAdditional: false },
  ];


  useEffect(() => {
    // Kickout solo si Firebase confirma que no hay usuario autenticado
    if (!authLoading && !user) {
      navigate("/authentication/sign-in");
    }
  }, [navigate, user, authLoading]);

  // Efecto para validar solapamiento en tiempo real cuando cambian los rangos de tiempo
  useEffect(() => {
    if (!showDateModal) return;

    if (modalTimeRanges.length >= 2) {
      // Validación sin cambiar estado para evitar bucle infinito
      const ranges = modalTimeRanges
        .filter(range => range.startText && range.endText && validateTimeFormat(range.startText) && validateTimeFormat(range.endText))
        .map(range => ({
          start: timeStringToDate(range.startText),
          end: timeStringToDate(range.endText)
        }))
        .filter(range => range.start && range.end && isValidTimeRange(range.start, range.end));

      let hasOverlap = false;
      for (let i = 0; i < ranges.length; i++) {
        for (let j = i + 1; j < ranges.length; j++) {
          const range1 = ranges[i];
          const range2 = ranges[j];

          if (range1.start.getTime() < range2.end.getTime() && range2.start.getTime() < range1.end.getTime()) {
            hasOverlap = true;
            break;
          }
        }
        if (hasOverlap) break;
      }

      setModalOverlapError(hasOverlap);
    } else {
      // Si hay menos de 2 rangos, no puede haber solapamiento
      setModalOverlapError(false);
    }
  }, [modalTimeRanges, showDateModal, timeStringToDate]);


  // Función para obtener disponibilidades recurrentes del usuario
  const fetchDisponibilidadesRecurrentes = async () => {
    const token = await getToken();
    if (!token) {
      navigate("/authentication/sign-in");
      return;
    }

    const profesionalId = localStorage.getItem('id');
    const baseURL = process.env.REACT_APP_BASE_URL;

    if (!profesionalId) {
      return null;
    }

    try {
      const response = await fetch(`${baseURL}/profesionales/${profesionalId}/disponibilidad`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        return data;
      }
    } catch (error) {
      console.error('Error al obtener disponibilidades recurrentes:', error);
    }

    return null;
  };

  useEffect(() => {
    const loadDisponibilidades = async () => {
      setIsLoading(true);

      try {
        const disponibilidades = await fetchDisponibilidadesRecurrentes();

        // Siempre empezar con todos los días de la semana con valores por defecto
        const allDaysTimeSlots = [...defaultTimeSlots];

        if (disponibilidades && disponibilidades.length > 0) {
          // Separar disponibilidades recurrentes y específicas
          const recurrentes = disponibilidades.filter(d => d.diaSemana);
          const especificas = disponibilidades.filter(d => !d.diaSemana && d.fechaEspecifica);

          // Procesar fechas específicas
          if (especificas.length > 0) {
            // Obtener fecha de hoy sin hora (solo fecha)
            const hoy = new Date();
            hoy.setHours(0, 0, 0, 0);

            const fechasEspecificas = especificas
              .map(disp => {
                // Crear fecha sin problemas de zona horaria
                const [year, month, day] = disp.fechaEspecifica.split('-');
                const fecha = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                fecha.setHours(0, 0, 0, 0);

                return {
                  id: disp.id,
                  date: fecha,
                  startTime: disp.horaInicio ? new Date(`2000-01-01T${disp.horaInicio}`) : null,
                  endTime: disp.horaFin ? new Date(`2000-01-01T${disp.horaFin}`) : null,
                  dateString: fecha.toLocaleDateString('es-ES', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  }),
                  serverId: disp.id
                };
              })
              .filter(item => item.date >= hoy); // Filtrar solo fechas de hoy o posteriores

            setSpecificDates(fechasEspecificas);
          }

          // Si hay disponibilidades recurrentes, procesar múltiples slots por día
          if (recurrentes.length > 0) {
            const diasSemana = {
              1: "L", 2: "M", 3: "Mi", 4: "J", 5: "V", 6: "S", 7: "D"
            };

            // Agrupar disponibilidades por día
            const disponibilidadesPorDia = {};
            recurrentes.forEach((disp) => {
              const dayLetter = diasSemana[disp.diaSemana];
              if (dayLetter) {
                if (!disponibilidadesPorDia[dayLetter]) {
                  disponibilidadesPorDia[dayLetter] = [];
                }
                disponibilidadesPorDia[dayLetter].push(disp);
              }
            });

            // Procesar cada día con sus múltiples slots
            Object.keys(disponibilidadesPorDia).forEach(dayLetter => {
              const slotsDelDia = disponibilidadesPorDia[dayLetter];

              // Crear slots para este día
              const nuevosSlots = [];
              slotsDelDia.forEach((disp, index) => {
                const slotId = index === 0 ?
                  allDaysTimeSlots.find(slot => slot.day === dayLetter)?.id ||
                  Math.max(...allDaysTimeSlots.map(slot => slot.id)) + index + 1 :
                  Math.max(...allDaysTimeSlots.map(slot => slot.id)) + index + 1;

                nuevosSlots.push({
                  id: slotId,
                  startTime: disp.horaInicio ? new Date(`2000-01-01T${disp.horaInicio}`) : null,
                  endTime: disp.horaFin ? new Date(`2000-01-01T${disp.horaFin}`) : null,
                  day: dayLetter,
                  isAdditional: index > 0
                });
              });

              // Reemplazar el slot original del día con los nuevos slots
              const dayIndex = allDaysTimeSlots.findIndex(slot => slot.day === dayLetter);
              if (dayIndex !== -1) {
                // Reemplazar el slot original con el primer slot de los datos del servidor
                allDaysTimeSlots[dayIndex] = nuevosSlots[0];

                // Agregar slots adicionales después del slot original
                nuevosSlots.slice(1).forEach((slot, index) => {
                  allDaysTimeSlots.splice(dayIndex + 1 + index, 0, slot);
                });
              }
            });
          }
        }

        setTimeSlots(allDaysTimeSlots);
        // Guardar copia profunda del estado original preservando los objetos Date
        const originalSlots = allDaysTimeSlots.map(slot => ({
          ...slot,
          startTime: slot.startTime ? new Date(slot.startTime) : null,
          endTime: slot.endTime ? new Date(slot.endTime) : null
        }));
        setOriginalTimeSlots(originalSlots);
      } catch (error) {
        console.error('Error al cargar disponibilidades:', error);
        // En caso de error, usar valores por defecto para todos los días
        setTimeSlots(defaultTimeSlots);
        // Guardar copia profunda del estado original preservando los objetos Date
        const originalDefaultSlots = defaultTimeSlots.map(slot => ({
          ...slot,
          startTime: slot.startTime ? new Date(slot.startTime) : null,
          endTime: slot.endTime ? new Date(slot.endTime) : null
        }));
        setOriginalTimeSlots(originalDefaultSlots);
      } finally {
        setIsLoading(false);
        setImageLoading(false);
      }
    };

    loadDisponibilidades();
    
    // Cargar bloqueos
    fetchBloqueos();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only: fetchBloqueos/fetchDisponibilidadesRecurrentes no están en useCallback
  }, []);

  // Cleanup del timeout cuando el componente se desmonte
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const handleAddTimeSlot = (dayToAdd, currentSlotId) => {
    const newId = Math.max(...timeSlots.map(slot => slot.id)) + 1;
    const newSlot = {
      id: newId,
      startTime: null,
      endTime: null,
      day: dayToAdd,
      isAdditional: true
    };

    // Encontrar el índice del slot actual
    const currentIndex = timeSlots.findIndex(slot => slot.id === currentSlotId);

    // Insertar el nuevo slot justo después del slot actual
    const newTimeSlots = [...timeSlots];
    newTimeSlots.splice(currentIndex + 1, 0, newSlot);

    setTimeSlots(newTimeSlots);

    // Guardar automáticamente después de agregar
    debouncedSave();
  };

  const handleRemoveTimeSlot = async (id) => {
    console.log('=== INICIO handleRemoveTimeSlot ===');
    console.log('ID del slot a eliminar:', id);

    const slotToRemove = timeSlots.find(slot => slot.id === id);
    if (!slotToRemove) {
      console.log('Slot no encontrado');
      return;
    }

    console.log('Slot encontrado:', slotToRemove);

    // Obtener todas las disponibilidades del servidor
    const existingDisponibilidades = await fetchDisponibilidadesConIds();
    console.log('Disponibilidades existentes en el servidor:', existingDisponibilidades);

    const diasSemana = { "L": 1, "M": 2, "Mi": 3, "J": 4, "V": 5, "S": 6, "D": 7 };
    const diaSemana = diasSemana[slotToRemove.day];

    if (slotToRemove.isAdditional) {
      // Si es un slot adicional, eliminar solo ese slot
      console.log('Eliminando slot adicional');

      // Función helper para convertir tiempo a string
      const timeToString = (time) => {
        if (!time) return null;
        if (time instanceof Date) {
          return time.toTimeString().slice(0, 5);
        }
        if (typeof time === 'string') {
          return time.slice(0, 5);
        }
        return null;
      };

      // Función helper para normalizar formato de hora (agregar :00 si no tiene segundos)
      const normalizeTimeFormat = (timeStr) => {
        if (!timeStr) return null;
        if (timeStr.length === 5) {
          return timeStr + ':00'; // Agregar segundos si no los tiene
        }
        return timeStr;
      };

      const horaInicio = timeToString(slotToRemove.startTime);
      const horaFin = timeToString(slotToRemove.endTime);
      const horaInicioNormalizada = normalizeTimeFormat(horaInicio);
      const horaFinNormalizada = normalizeTimeFormat(horaFin);

      // Buscar la disponibilidad específica en el servidor
      const disponibilidadToDelete = existingDisponibilidades.find(disp =>
        disp.diaSemana === diaSemana &&
        disp.horaInicio === horaInicioNormalizada &&
        disp.horaFin === horaFinNormalizada
      );

      // Si existe en el servidor, eliminarla
      if (disponibilidadToDelete) {
        console.log('Eliminando disponibilidad del servidor con ID:', disponibilidadToDelete.id);
        const deleteSuccess = await deleteAvailabilityById(disponibilidadToDelete.id);
        if (!deleteSuccess) {
          console.error('Error al eliminar disponibilidad del servidor');
          setShowErrorMessage(true);
          return;
        }
      }

      // Eliminar del estado local
      setTimeSlots(timeSlots.filter(slot => slot.id !== id));
      const newOriginalSlots = originalTimeSlots.filter(slot => slot.id !== id);
      setOriginalTimeSlots(newOriginalSlots);

    } else {
      // Si es el slot principal, eliminar slots adicionales y resetear el principal
      console.log('Reseteando día:', slotToRemove.day);

      // Buscar todas las disponibilidades de este día en el servidor
      const disponibilidadesDelDia = existingDisponibilidades.filter(disp =>
        disp.diaSemana === diaSemana
      );

      console.log(`Encontradas ${disponibilidadesDelDia.length} disponibilidades para el día ${slotToRemove.day}`);

      // Eliminar todas las disponibilidades del día del servidor
      for (const disp of disponibilidadesDelDia) {
        console.log('Eliminando disponibilidad del servidor con ID:', disp.id);
        const deleteSuccess = await deleteAvailabilityById(disp.id);
        if (!deleteSuccess) {
          console.error('Error al eliminar disponibilidad del servidor');
          setShowErrorMessage(true);
          return;
        }
      }

      // Obtener slots del día
      const slotsDelDia = timeSlots.filter(slot => slot.day === slotToRemove.day);
      const slotsAdicionalesDelDia = slotsDelDia.filter(slot => slot.isAdditional);

      console.log(`Eliminando ${slotsAdicionalesDelDia.length} slots adicionales del día ${slotToRemove.day}`);

      // Eliminar solo los slots adicionales del estado local
      setTimeSlots(timeSlots.filter(slot => !(slot.day === slotToRemove.day && slot.isAdditional)));
      const newOriginalSlots = originalTimeSlots.filter(slot => !(slot.day === slotToRemove.day && slot.isAdditional));
      setOriginalTimeSlots(newOriginalSlots);

      // Resetear el slot principal (limpiar sus valores)
      setTimeSlots(prev => prev.map(slot =>
        slot.id === id
          ? { ...slot, startTime: null, endTime: null }
          : slot
      ));

      const updatedOriginalSlots = newOriginalSlots.map(slot =>
        slot.id === id
          ? { ...slot, startTime: null, endTime: null }
          : slot
      );
      setOriginalTimeSlots(updatedOriginalSlots);

      // Limpiar los valores de texto de los inputs de este día
      const keysToRemove = Object.keys(timeInputValues).filter(key =>
        slotsDelDia.some(slot => key.includes(`${slot.id}-`))
      );
      setTimeInputValues(prev => {
        const newValues = { ...prev };
        keysToRemove.forEach(key => delete newValues[key]);
        return newValues;
      });
    }

    console.log('=== FIN handleRemoveTimeSlot ===');

    // Mostrar mensaje de éxito
    setSuccessMessage('Disponibilidad eliminada con éxito');
    setShowSuccessMessage(true);
  };

  // eslint-disable-next-line no-unused-vars -- reservado para uso futuro
  const _handleTimeSlotChange = (id, field, value) => {
    console.log(`handleTimeSlotChange: id=${id}, field=${field}, value=`, value);

    const newTimeSlots = timeSlots.map(slot =>
      slot.id === id ? { ...slot, [field]: value } : slot
    );

    console.log('Nuevo estado timeSlots:', newTimeSlots.find(slot => slot.id === id));

    setTimeSlots(newTimeSlots);

    // Guardar automáticamente después del cambio usando el nuevo estado
    debouncedSaveWithState(newTimeSlots);
  };

  const handleAddDailyHours = () => {
    // Limpiar cualquier mensaje de error previo
    setShowErrorMessage(false);
    setShowOverlapMessage(false);
    setHasOverlapError(false);
    setModalOverlapError(false);
    // Abrir modal para seleccionar fecha específica
    setShowDateModal(true);
    // Reset estados del modal anterior y del nuevo
    setSelectedDate(null);
    setSelectedStartTime(null);
    setSelectedEndTime(null);
    setModalStartTimeText('');
    setModalEndTimeText('');
    // Crear fecha local sin problemas de zona horaria
    const now = new Date();
    const localYear = now.getFullYear();
    const localMonth = now.getMonth();
    setCalendarYear(localYear);
    setCalendarMonth(localMonth);
    setSelectedDateKey(null);
    setModalTimeRanges([{ id: 1, startText: '', endText: '' }]);
  };

  const handleCloseDateModal = () => {
    setShowDateModal(false);
    // Limpiar todos los estados del modal
    setSelectedDate(null);
    setSelectedStartTime(null);
    setSelectedEndTime(null);
    setModalStartTimeText('');
    setModalEndTimeText('');
    setSelectedDateKey(null);
    setModalTimeRanges([{ id: 1, startText: '', endText: '' }]);
    // Limpiar cualquier mensaje de error
    setShowErrorMessage(false);
    setShowOverlapMessage(false);
    setHasOverlapError(false);
    setModalOverlapError(false);
  };

  // Función para validar si una hora ya pasó en el día actual
  const isTimeInPast = (timeString) => {
    if (!timeString || !validateTimeFormat(timeString)) return false;

    const now = new Date();
    const [hours, minutes] = timeString.split(':').map(Number);
    const timeToCheck = new Date();
    timeToCheck.setHours(hours, minutes, 0, 0);

    return timeToCheck < now;
  };

  // Validación de rangos del modal
  const isValidModalRange = (range) => {
    if (!validateTimeFormat(range.startText) || !validateTimeFormat(range.endText)) return false;
    const start = timeStringToDate(range.startText);
    const end = timeStringToDate(range.endText);
    if (!start || !end) return false;

    // Verificar que la hora de inicio no sea en el pasado (solo para el día actual)
    const today = new Date();
    const isToday = selectedDateKey ? (() => {
      const [y, m, d] = selectedDateKey.split('-').map(n => parseInt(n));
      const dateObj = new Date(y, m - 1, d);
      const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const selectedDateOnly = new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate());
      return selectedDateOnly.getTime() === todayOnly.getTime();
    })() : false;

    if (isToday && isTimeInPast(range.startText)) {
      return false;
    }

    return isValidTimeRange(start, end);
  };

  // Función para obtener el mensaje de error específico del modal
  const getModalErrorMessage = () => {
    // Verificar primero si hay error de solapamiento específico
    if (hasOverlapError) {
      return "Las horas seleccionadas se solapan con un horario ya existente para esta fecha. Por favor, elija un horario diferente.";
    }

    if (selectedDateKey === null) {
      return "Por favor seleccione una fecha.";
    }

    if (modalTimeRanges.length === 0) {
      return "Por favor agregue al menos un rango de horas.";
    }

    // Verificar cada rango de tiempo
    for (const range of modalTimeRanges) {
      if (!validateTimeFormat(range.startText) || !validateTimeFormat(range.endText)) {
        return "Por favor complete todos los campos de hora con formato válido (HH:mm).";
      }

      const start = timeStringToDate(range.startText);
      const end = timeStringToDate(range.endText);
      if (!start || !end) {
        return "Por favor complete todos los campos de hora con formato válido (HH:mm).";
      }

      // Verificar si la hora de inicio es en el pasado (solo para el día actual)
      const today = new Date();
      const isToday = selectedDateKey ? (() => {
        const [y, m, d] = selectedDateKey.split('-').map(n => parseInt(n));
        const dateObj = new Date(y, m - 1, d);
        const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const selectedDateOnly = new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate());
        return selectedDateOnly.getTime() === todayOnly.getTime();
      })() : false;

      if (isToday && isTimeInPast(range.startText)) {
        return "No se pueden agregar horarios que ya pasaron. Por favor seleccione una hora futura.";
      }

      if (!isValidTimeRange(start, end)) {
        return "La hora de fin debe ser al menos 1 hora mayor que la hora de inicio.";
      }
    }

    return "Error desconocido.";
  };

  // Función para detectar solapamiento en tiempo real en el modal (sin cambiar estado)
  const checkModalOverlap = () => {
    if (modalTimeRanges.length < 2) {
      return false;
    }

    // Convertir todos los rangos a objetos Date para comparar
    const ranges = modalTimeRanges
      .filter(range => range.startText && range.endText && validateTimeFormat(range.startText) && validateTimeFormat(range.endText))
      .map(range => ({
        start: timeStringToDate(range.startText),
        end: timeStringToDate(range.endText)
      }))
      .filter(range => range.start && range.end && isValidTimeRange(range.start, range.end));

    // Verificar solapamiento entre todos los rangos
    for (let i = 0; i < ranges.length; i++) {
      for (let j = i + 1; j < ranges.length; j++) {
        const range1 = ranges[i];
        const range2 = ranges[j];

        // Dos intervalos se solapan si: start1 < end2 && start2 < end1
        if (range1.start.getTime() < range2.end.getTime() && range2.start.getTime() < range1.end.getTime()) {
          return true;
        }
      }
    }

    return false;
  };

  const canSaveBulk = () => {
    if (selectedDateKey === null) return false;
    if (modalTimeRanges.length === 0) return false;
    if (checkModalOverlap()) return false; // Verificar solapamiento en tiempo real
    return modalTimeRanges.every(isValidModalRange);
  };

  const handleOpenConfirmSaveBulk = () => {
    if (!canSaveBulk()) {
      setShowErrorMessage(true);
      return;
    }
    setShowConfirmSaveBulk(true);
  };

  const handleCloseConfirmSaveBulk = () => {
    setShowConfirmSaveBulk(false);
  };

  const handleConfirmSaveBulk = async () => {
    setShowConfirmSaveBulk(false);
    await handleSaveSpecificDatesBulk();
  };

  const handleSaveSpecificDatesBulk = async () => {
    if (!canSaveBulk()) {
      setShowErrorMessage(true);
      return;
    }

    const createdItems = [];
    if (selectedDateKey) {
      const [y, m, d] = selectedDateKey.split('-').map(n => parseInt(n));
      const dateObj = new Date(y, m - 1, d);
      for (const range of modalTimeRanges) {
        const start = timeStringToDate(range.startText);
        const end = timeStringToDate(range.endText);
        const newSpecificDate = {
          id: Date.now() + Math.random(),
          date: dateObj,
          startTime: start,
          endTime: end,
          dateString: dateObj.toLocaleDateString('es-ES', {
            weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
          })
        };
        createdItems.push(newSpecificDate);
        setSpecificDates(prev => [...prev, newSpecificDate]);
        await saveSpecificDateToServer(newSpecificDate);
      }
    }

    handleCloseDateModal();
    setSuccessMessage('Fecha específica agregada con éxito');
    setShowSuccessMessage(true);
  };

  // eslint-disable-next-line no-unused-vars -- reservado para uso futuro
  const _handleSaveSpecificDate = async () => {
    // Validación más robusta
    if (!selectedDate || !selectedStartTime || !selectedEndTime) {
      setShowErrorMessage(true);
      return;
    }

    // Validar que la hora de fin sea al menos 1 hora mayor que la hora de inicio
    if (!isValidTimeRange(selectedStartTime, selectedEndTime)) {
      setShowErrorMessage(true);
      return;
    }

    // Validar que la fecha no sea en el pasado ni hoy
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDateOnly = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
    if (selectedDateOnly <= today) {
      setShowErrorMessage(true);
      return;
    }

    // Validar que no haya solapamiento con horarios existentes para la misma fecha
    const hasOverlap = specificDates.some(existingDate => {
      // Comparar fechas (solo día, mes y año)
      const existingDateOnly = new Date(existingDate.date.getFullYear(), existingDate.date.getMonth(), existingDate.date.getDate());
      const selectedDateOnly = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());

      if (existingDateOnly.getTime() !== selectedDateOnly.getTime()) {
        return false; // No es la misma fecha
      }

      // Verificar solapamiento de horarios
      const existingStart = existingDate.startTime.getTime();
      const existingEnd = existingDate.endTime.getTime();
      const newStart = selectedStartTime.getTime();
      const newEnd = selectedEndTime.getTime();

      // Dos intervalos se solapan si: start1 < end2 && start2 < end1
      return newStart < existingEnd && existingStart < newEnd;
    });

    if (hasOverlap) {
      setHasOverlapError(true);
      setShowErrorMessage(true);
      return;
    }

    const newSpecificDate = {
      id: Date.now(), // ID temporal
      date: selectedDate,
      startTime: selectedStartTime,
      endTime: selectedEndTime,
      dateString: selectedDate.toLocaleDateString('es-ES', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    };

    // Agregar a la lista local
    setSpecificDates(prev => [...prev, newSpecificDate]);

    // Guardar en el servidor
    await saveSpecificDateToServer(newSpecificDate);

    // Cerrar modal y limpiar estados
    handleCloseDateModal();
    setSuccessMessage('Fecha específica agregada con éxito');
    setShowSuccessMessage(true);
  };

  const handleRemoveSpecificDate = async (id) => {
    const dateToRemove = specificDates.find(date => date.id === id);
    if (!dateToRemove) return;

    // Eliminar del servidor si tiene ID del servidor
    if (dateToRemove.serverId) {
      await deleteSpecificDateFromServer(dateToRemove.serverId);
    }

    // Eliminar de la lista local
    setSpecificDates(prev => prev.filter(date => date.id !== id));
    setSuccessMessage('Fecha específica eliminada con éxito');
    setShowSuccessMessage(true);
  };

  // Función para guardar fecha específica en el servidor
  const saveSpecificDateToServer = async (specificDate) => {
    const token = await getToken();
    if (!token) {
      navigate("/authentication/sign-in");
      return false;
    }

    const profesionalId = localStorage.getItem('id');
    const baseURL = process.env.REACT_APP_BASE_URL;

    if (!profesionalId) {
      console.error('JWT o profesionalId no encontrado');
      return;
    }

    try {
      // Función helper para convertir tiempo a string
      const timeToString = (time) => {
        if (!time) return null;
        if (time instanceof Date) {
          return time.toTimeString().slice(0, 5);
        }
        if (typeof time === 'string') {
          return time.slice(0, 5);
        }
        return null;
      };

      // Validar que la fecha sea válida
      if (!specificDate.date || isNaN(specificDate.date.getTime())) {
        console.error('Fecha específica inválida:', specificDate.date);
        return false;
      }

      // Crear fecha en formato YYYY-MM-DD sin problemas de zona horaria
      const year = specificDate.date.getFullYear();
      const month = String(specificDate.date.getMonth() + 1).padStart(2, '0');
      const day = String(specificDate.date.getDate()).padStart(2, '0');
      const fechaFormateada = `${year}-${month}-${day}`;

      const requestBody = {
        profesionalId: parseInt(profesionalId),
        diaSemana: null, // Para fechas específicas, diaSemana debe ser null
        fechaEspecifica: fechaFormateada, // Formato YYYY-MM-DD sin problemas de zona horaria
        horaInicio: timeToString(specificDate.startTime),
        horaFin: timeToString(specificDate.endTime)
      };

      console.log('Guardando fecha específica:', requestBody);
      console.log('Fecha original:', specificDate.date);
      console.log('Fecha formateada:', fechaFormateada);
      console.log('Validación - diaSemana es null:', requestBody.diaSemana === null);
      console.log('Validación - fechaEspecifica:', requestBody.fechaEspecifica);

      const response = await fetch(`${baseURL}/crear-disponibilidad`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Fecha específica guardada exitosamente:', data);

        // Actualizar el ID del servidor en el estado local
        setSpecificDates(prev => prev.map(date =>
          date.id === specificDate.id
            ? { ...date, serverId: data.id }
            : date
        ));

        return true;
      } else {
        const errorData = await response.json();
        console.error('Error al guardar fecha específica:', errorData);
        return false;
      }
    } catch (error) {
      console.error('Error al guardar fecha específica:', error);
      return false;
    }
  };

  // Función para eliminar fecha específica del servidor
  const deleteSpecificDateFromServer = async (serverId) => {
    const token = await getToken();
    if (!token) {
      navigate("/authentication/sign-in");
      return false;
    }

    const baseURL = process.env.REACT_APP_BASE_URL;

    if (!serverId) {
      console.error('JWT o serverId no encontrado');
      return false;
    }

    try {
      const response = await fetch(`${baseURL}/eliminar-disponibilidad/${serverId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        console.log('Fecha específica eliminada exitosamente del servidor');
        return true;
      } else {
        const errorData = await response.json();
        console.error('Error al eliminar fecha específica:', errorData);
        return false;
      }
    } catch (error) {
      console.error('Error al eliminar fecha específica:', error);
      return false;
    }
  };

  // Función para validar si un horario está completo
  const isTimeSlotComplete = (slot) => {
    return slot.startTime !== null &&
      slot.endTime !== null &&
      slot.startTime instanceof Date &&
      slot.endTime instanceof Date;
  };

  // Función para determinar si un día tiene múltiples slots
  // eslint-disable-next-line no-unused-vars -- reservado para uso futuro
  const _hasMultipleSlotsForDay = (day) => {
    return timeSlots.filter(slot => slot.day === day).length > 1;
  };

  // Función para determinar si mostrar el botón de eliminar
  const shouldShowRemoveButton = (slot) => {
    // Mostrar siempre si es un slot adicional O si es el slot principal pero tiene valores (para poder limpiarlos)
    return slot.isAdditional || (!slot.isAdditional && (slot.startTime !== null || slot.endTime !== null));
  };

  // Función para determinar si mostrar el botón de agregar
  const shouldShowAddButton = (slot) => {
    // Mostrar si no es un slot adicional (solo en el primer slot de cada día)
    return !slot.isAdditional;
  };

  // Función para comparar dos horarios y detectar si han cambiado
  const hasTimeSlotChanged = (currentSlot, originalSlot) => {
    if (!originalSlot) return true; // Si no existe en el original, es nuevo

    // Función helper para convertir tiempo a string de forma segura
    const timeToString = (time) => {
      if (!time) return null;
      if (time instanceof Date) {
        return time.toTimeString().slice(0, 5);
      }
      if (typeof time === 'string') {
        return time.slice(0, 5);
      }
      return null;
    };

    const currentStart = timeToString(currentSlot.startTime);
    const currentEnd = timeToString(currentSlot.endTime);
    const originalStart = timeToString(originalSlot.startTime);
    const originalEnd = timeToString(originalSlot.endTime);

    return currentStart !== originalStart || currentEnd !== originalEnd;
  };

  // Función para obtener disponibilidades existentes con sus IDs
  const fetchDisponibilidadesConIds = async () => {
    console.log('=== INICIO fetchDisponibilidadesConIds ===');

    const token = await getToken();
    if (!token) {
      navigate("/authentication/sign-in");
      return;
    }

    const profesionalId = localStorage.getItem('id');
    const baseURL = process.env.REACT_APP_BASE_URL;

    console.log('Profesional ID:', profesionalId);
    console.log('Base URL:', baseURL);

    if (!profesionalId) {
      console.log('ProfesionalId no encontrado');
      return [];
    }

    const url = `${baseURL}/profesionales/${profesionalId}/disponibilidad`;
    console.log('URL de consulta:', url);

    try {
      console.log('Enviando petición GET...');
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Respuesta recibida:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Disponibilidades obtenidas:', data);
        console.log('=== FIN fetchDisponibilidadesConIds (éxito) ===');
        return data;
      } else {
        const errorData = await response.json();
        console.error('Error al obtener disponibilidades:', errorData);
        console.log('=== FIN fetchDisponibilidadesConIds (error) ===');
      }
    } catch (error) {
      console.error('Error al obtener disponibilidades con IDs:', error);
      console.log('=== FIN fetchDisponibilidadesConIds (excepción) ===');
    }

    return [];
  };

  // Función para eliminar una disponibilidad específica usando el endpoint DELETE
  const deleteAvailabilityById = async (disponibilidadId) => {
    console.log('=== INICIO deleteAvailabilityById ===');
    console.log('ID de disponibilidad a eliminar:', disponibilidadId);

    const token = await getToken();
    if (!token) {
      navigate("/authentication/sign-in");
      return false;
    }

   
    

    const baseURL = process.env.REACT_APP_BASE_URL;

    console.log('Token encontrado:', !!token);
    console.log('Base URL:', baseURL);

    if (!disponibilidadId) {
      console.error('JWT o ID de disponibilidad no encontrado');
      return false;
    }

    const url = `${baseURL}/eliminar-disponibilidad/${disponibilidadId}`;
    console.log('URL de eliminación:', url);

    try {
      console.log('Enviando petición DELETE...');
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Respuesta recibida:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Disponibilidad eliminada exitosamente:', data);
        console.log('=== FIN deleteAvailabilityById (éxito) ===');
        return true;
      } else {
        const errorData = await response.json();
        console.error('Error al eliminar disponibilidad:', errorData);
        console.log('=== FIN deleteAvailabilityById (error) ===');
        return false;
      }
    } catch (error) {
      console.error('Error al eliminar disponibilidad:', error);
      console.log('=== FIN deleteAvailabilityById (excepción) ===');
      return false;
    }
  };

  // Función para verificar y eliminar disponibilidades que ya no existen en el estado local
  const cleanupDeletedDisponibilidades = async () => {
    const token = await getToken();
    if (!token) {
      navigate("/authentication/sign-in");
      return;
    }

    const profesionalId = localStorage.getItem('id');

    if (!profesionalId) {
      return;
    }

    try {
      // Obtener disponibilidades existentes en el servidor
      const existingDisponibilidades = await fetchDisponibilidadesConIds();

      // Obtener disponibilidades actuales en el estado local
      const diasSemana = { "L": 1, "M": 2, "Mi": 3, "J": 4, "V": 5, "S": 6, "D": 7 };

      // Función helper para convertir tiempo a string
      const timeToString = (time) => {
        if (!time) return null;
        if (time instanceof Date) {
          return time.toTimeString().slice(0, 5);
        }
        if (typeof time === 'string') {
          return time.slice(0, 5);
        }
        return null;
      };

      // Función helper para normalizar formato de hora (agregar :00 si no tiene segundos)
      const normalizeTimeFormat = (timeStr) => {
        if (!timeStr) return null;
        if (timeStr.length === 5) {
          return timeStr + ':00'; // Agregar segundos si no los tiene
        }
        return timeStr;
      };

      // Crear un mapa de disponibilidades actuales en el estado local
      const currentDisponibilidades = new Map();
      timeSlots.forEach(slot => {
        if (slot.startTime && slot.endTime) {
          const horaInicio = timeToString(slot.startTime);
          const horaFin = timeToString(slot.endTime);
          const diaSemana = diasSemana[slot.day];
          const horaInicioNormalizada = normalizeTimeFormat(horaInicio);
          const horaFinNormalizada = normalizeTimeFormat(horaFin);
          const key = `${diaSemana}-${horaInicioNormalizada}-${horaFinNormalizada}`;
          currentDisponibilidades.set(key, true);
        }
      });

      // Encontrar disponibilidades en el servidor que ya no existen en el estado local
      const disponibilidadesToDelete = existingDisponibilidades.filter(disp => {
        const key = `${disp.diaSemana}-${disp.horaInicio}-${disp.horaFin}`;
        return !currentDisponibilidades.has(key);
      });

      // Eliminar las disponibilidades que ya no existen
      for (const disp of disponibilidadesToDelete) {
        console.log('Eliminando disponibilidad obsoleta:', disp);
        await deleteAvailabilityById(disp.id);
      }

      if (disponibilidadesToDelete.length > 0) {
        console.log(`${disponibilidadesToDelete.length} disponibilidades obsoletas eliminadas`);
      }

    } catch (error) {
      console.error('Error al limpiar disponibilidades obsoletas:', error);
    }
  };

  // Función para guardar la disponibilidad automáticamente
  const saveAvailability = async () => {
    return saveAvailabilityWithState(timeSlots);
  };

  // Función para guardar la disponibilidad con un estado específico
  const saveAvailabilityWithState = async (specificTimeSlots) => {
    console.log('=== INICIO saveAvailabilityWithState ===');
    console.log('Estado específico timeSlots:', specificTimeSlots);
    console.log('Estado original originalTimeSlots:', originalTimeSlots);

    // Verificar si hay al menos un horario completo antes de intentar guardar
    const validSlots = specificTimeSlots.filter(isTimeSlotComplete);

    console.log('Slots válidos:', validSlots);

    if (validSlots.length === 0) {
      // No hay horarios completos, no intentar guardar
      console.log('No hay slots válidos, saliendo');
      return;
    }

    setIsSaving(true);

    try {
      const token = await getToken();
      if (!token) {
        navigate("/authentication/sign-in");
        return;
      }
      const profesionalId = localStorage.getItem('id');
      const baseURL = process.env.REACT_APP_BASE_URL;

      if (!profesionalId) {
        setShowErrorMessage(true);
        return;
      }

      // Primero limpiar disponibilidades obsoletas
      await cleanupDeletedDisponibilidades();

      // Obtener disponibilidades existentes con sus IDs (después de la limpieza)
      const existingDisponibilidades = await fetchDisponibilidadesConIds();
      console.log('Disponibilidades existentes en el servidor:', existingDisponibilidades);

      // Preparar los datos para enviar
      const diasSemana = {
        "L": 1, "M": 2, "Mi": 3, "J": 4, "V": 5, "S": 6, "D": 7
      };

      const results = [];

      // Procesar cada slot válido
      for (const slot of validSlots) {
        const originalSlot = originalTimeSlots.find(orig => orig.id === slot.id);

        // Función helper para convertir tiempo a string de forma segura (reutilizando la lógica existente)
        const timeToString = (time) => {
          if (!time) return null;
          if (time instanceof Date) {
            return time.toTimeString().slice(0, 5);
          }
          if (typeof time === 'string') {
            return time.slice(0, 5);
          }
          return null;
        };

        console.log(`=== DEBUG SLOT ${slot.day} (id: ${slot.id}) ===`);
        console.log('Slot completo:', slot);
        console.log('Slot.startTime:', slot.startTime);
        console.log('Slot.endTime:', slot.endTime);
        console.log('timeToString(slot.startTime):', timeToString(slot.startTime));
        console.log('timeToString(slot.endTime):', timeToString(slot.endTime));
        console.log('Original slot:', originalSlot);
        if (originalSlot) {
          console.log('Original slot.startTime:', originalSlot.startTime);
          console.log('Original slot.endTime:', originalSlot.endTime);
          console.log('timeToString(originalSlot.startTime):', timeToString(originalSlot.startTime));
          console.log('timeToString(originalSlot.endTime):', timeToString(originalSlot.endTime));
        }

        const hasChanged = hasTimeSlotChanged(slot, originalSlot);
        console.log(`Procesando slot ${slot.day}:`, {
          current: {
            start: timeToString(slot.startTime),
            end: timeToString(slot.endTime)
          },
          original: {
            start: originalSlot ? timeToString(originalSlot.startTime) : 'no existe',
            end: originalSlot ? timeToString(originalSlot.endTime) : 'no existe'
          },
          hasChanged: hasChanged
        });

        console.log(`¿Slot ${slot.day} ha cambiado? ${hasChanged}`);

        // Solo procesar si el slot ha cambiado o es nuevo
        if (hasChanged) {
          const diaSemana = diasSemana[slot.day];
          const horaInicio = timeToString(slot.startTime);
          const horaFin = timeToString(slot.endTime);

          // Buscar si existe una disponibilidad para este día y horario específico
          const existingForDayAndTime = existingDisponibilidades.find(disp =>
            disp.diaSemana === diaSemana &&
            disp.horaInicio === horaInicio &&
            disp.horaFin === horaFin
          );

          if (existingForDayAndTime) {
            // Ya existe exactamente esta disponibilidad, no hacer nada
            console.log(`Disponibilidad para ${slot.day} ${horaInicio}-${horaFin} ya existe en el servidor, omitiendo`);
            continue;
          }

          // Buscar si existe alguna disponibilidad para este día que necesite actualización
          const existingForDay = existingDisponibilidades.find(disp => disp.diaSemana === diaSemana);

          if (existingForDay && !slot.isAdditional) {
            // Es el slot principal del día, actualizar la disponibilidad existente
            const updateData = {
              diaSemana: diaSemana,
              fechaEspecifica: null,
              horaInicio: horaInicio,
              horaFin: horaFin
            };

            console.log('Actualizando disponibilidad principal:', updateData);

            try {
              const response = await fetch(`${baseURL}/actualizar-disponibilidad/${existingForDay.id}`, {
                method: 'PUT',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify(updateData),
              });

              if (response.ok) {
                results.push({ success: true, action: 'updated', data: await response.json() });
              } else {
                const errorData = await response.json();
                console.warn('Error al actualizar disponibilidad:', errorData);
                results.push({ success: false, action: 'update_failed', error: errorData.message });
              }
            } catch (error) {
              console.error('Error al actualizar disponibilidad:', error);
              results.push({ success: false, action: 'update_failed', error: error.message });
            }
          } else {
            // Crear nueva disponibilidad (slot adicional o nuevo día)
            const requestBody = {
              profesionalId: parseInt(profesionalId),
              diaSemana: diaSemana,
              fechaEspecifica: null,
              horaInicio: horaInicio,
              horaFin: horaFin
            };

            console.log('Creando nueva disponibilidad:', requestBody);

            try {
              const response = await fetch(`${baseURL}/crear-disponibilidad`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody),
              });

              if (response.ok) {
                results.push({ success: true, action: 'created', data: await response.json() });
              } else {
                const errorData = await response.json();
                console.warn('Error al crear disponibilidad:', errorData);
                results.push({ success: false, action: 'create_failed', error: errorData.message });
              }
            } catch (error) {
              console.error('Error al crear disponibilidad:', error);
              results.push({ success: false, action: 'create_failed', error: error.message });
            }
          }
        }
      }

      // Verificar resultados
      const successful = results.filter(r => r.success);
      const failed = results.filter(r => !r.success);

      if (successful.length > 0) {
        // Actualizar el estado original con los cambios guardados
        // Crear una copia profunda que preserve los objetos Date correctamente
        const newOriginalSlots = specificTimeSlots.map(slot => ({
          ...slot,
          startTime: slot.startTime ? new Date(slot.startTime) : null,
          endTime: slot.endTime ? new Date(slot.endTime) : null
        }));
        setOriginalTimeSlots(newOriginalSlots);

        if (failed.length > 0) {
          // Algunas se procesaron, algunas fallaron
          setSuccessMessage('Disponibilidad guardada exitosamente');
          setShowSuccessMessage(true);
          console.log(`${successful.length} disponibilidades procesadas, ${failed.length} fallaron`);
        } else {
          // Todas se procesaron exitosamente
          setSuccessMessage('Disponibilidad guardada exitosamente');
          setShowSuccessMessage(true);
        }
      } else if (results.length === 0) {
        // No había cambios que procesar
        console.log('No hay cambios que guardar');
      } else {
        // Ninguna se pudo procesar
        setShowErrorMessage(true);
      }

    } catch (error) {
      console.error('Error al guardar disponibilidad:', error);
      setShowErrorMessage(true);
    } finally {
      setIsSaving(false);
    }
  };

  // Función para guardar con debounce usando el estado actual
  const debouncedSave = () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      // Solo intentar guardar si hay al menos un horario completo
      const validSlots = timeSlots.filter(isTimeSlotComplete);
      if (validSlots.length > 0) {
        saveAvailability();
      }
    }, 1000); // Esperar 1 segundo después del último cambio
  };

  // Función para guardar con debounce usando un estado específico
  const debouncedSaveWithState = (specificTimeSlots) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      // Solo intentar guardar si hay al menos un horario completo
      const validSlots = specificTimeSlots.filter(isTimeSlotComplete);
      if (validSlots.length > 0) {
        saveAvailabilityWithState(specificTimeSlots);
      }
    }, 1000); // Esperar 1 segundo después del último cambio
  };

  const handleImageLoad = () => {
    setImageLoading(false);
  };

  const handleImageError = () => {
    setImageLoading(false);
  };

  const handleNotificationsClick = (event) => {
    setNotificationsAnchorEl(event.currentTarget);
  };

  const handleNotificationsClose = () => {
    setNotificationsAnchorEl(null);
  };

  // Componente de skeleton completo
  const SkeletonContent = () => (
    <>
      <style>
        {`
          body {
            overflow-x: hidden;
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
          body::-webkit-scrollbar {
            display: none;
          }
          html {
            overflow-x: hidden;
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
          html::-webkit-scrollbar {
            display: none;
          }
        `}
      </style>
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: "#FFFFFF",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "20px",
          paddingBottom: "100px",
          "&::-webkit-scrollbar": {
            display: "none",
          },
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        {/* Header skeleton */}
        <Box
          sx={{
            width: "100%",
            maxWidth: "800px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginBottom: "40px",
            marginTop: "20px",
            position: "relative",
          }}
        >
          <SkeletonLoader
            variant="text"
            width="200px"
            height="32px"
            sx={{ fontSize: "24px" }}
          />
          <SkeletonLoader
            variant="circular"
            width="24px"
            height="24px"
            sx={{
              position: "absolute",
              right: 0,
            }}
          />
        </Box>

        {/* Card skeleton */}
        <Card
          sx={{
            width: "100%",
            maxWidth: "800px",
            backgroundColor: "#fff",
            borderRadius: "12px",
            boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
            padding: "24px",
          }}
        >
          {/* Sección superior skeleton */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "24px",
              paddingBottom: "16px",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            {/* Ubicación skeleton */}
            <Box sx={{ flex: 1 }}>
              <SkeletonLoader
                variant="text"
                width="80px"
                height="20px"
                sx={{ marginBottom: "8px" }}
              />
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <SkeletonLoader
                  variant="rectangular"
                  width="20px"
                  height="20px"
                  sx={{ borderRadius: "4px" }}
                />
                <SkeletonLoader
                  variant="text"
                  width="100px"
                  height="16px"
                />
              </Box>
            </Box>

            {/* Duración skeleton */}
            <Box sx={{ flex: 1 }}>
              <SkeletonLoader
                variant="text"
                width="60px"
                height="20px"
                sx={{ marginBottom: "8px" }}
              />
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <SkeletonLoader
                  variant="circular"
                  width="16px"
                  height="16px"
                />
                <SkeletonLoader
                  variant="text"
                  width="50px"
                  height="16px"
                />
              </Box>
            </Box>
          </Box>

          {/* Sección de horario skeleton */}
          <Box sx={{ marginBottom: "24px" }}>
            <SkeletonLoader
              variant="text"
              width="180px"
              height="20px"
              sx={{ marginBottom: "16px" }}
            />

            {/* Horas semanales skeleton */}
            <Box sx={{ marginBottom: "16px" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                <SkeletonLoader
                  variant="circular"
                  width="16px"
                  height="16px"
                />
                <SkeletonLoader
                  variant="text"
                  width="120px"
                  height="16px"
                />
              </Box>
              <SkeletonLoader
                variant="text"
                width="250px"
                height="14px"
                sx={{ marginLeft: "24px" }}
              />
            </Box>

            {/* Lista de slots skeleton */}
            <Box sx={{ marginLeft: "28px" }}>
              {[1, 2, 3, 4, 5, 6, 7].map((index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    marginBottom: "8px",
                  }}
                >
                  <SkeletonLoader
                    variant="circular"
                    width="32px"
                    height="32px"
                  />
                  <SkeletonLoader
                    variant="rectangular"
                    width="60px"
                    height="24px"
                    sx={{ borderRadius: "4px" }}
                  />
                  <SkeletonLoader
                    variant="text"
                    width="8px"
                    height="16px"
                  />
                  <SkeletonLoader
                    variant="rectangular"
                    width="60px"
                    height="24px"
                    sx={{ borderRadius: "4px" }}
                  />
                  <SkeletonLoader
                    variant="circular"
                    width="24px"
                    height="24px"
                  />
                  <SkeletonLoader
                    variant="circular"
                    width="24px"
                    height="24px"
                  />
                </Box>
              ))}
            </Box>
          </Box>

          {/* Sección de horas según el día skeleton */}
          <Box sx={{ marginTop: "32px" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                  <SkeletonLoader
                    variant="circular"
                    width="16px"
                    height="16px"
                  />
                  <SkeletonLoader
                    variant="text"
                    width="140px"
                    height="16px"
                  />
                </Box>
                <SkeletonLoader
                  variant="text"
                  width="200px"
                  height="14px"
                  sx={{ marginLeft: "24px" }}
                />
              </Box>
              <SkeletonLoader
                variant="rectangular"
                width="80px"
                height="32px"
                sx={{ borderRadius: "40px" }}
              />
            </Box>
          </Box>
        </Card>
      </Box>
    </>
  );

  // Mostrar skeleton mientras carga
  if (isLoading) {
    return <SkeletonContent />;
  }

  return (
    <>
      <style>
        {`
          body {
            overflow-x: hidden;
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
          body::-webkit-scrollbar {
            display: none;
          }
          html {
            overflow-x: hidden;
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
          html::-webkit-scrollbar {
            display: none;
          }
          
        `}
      </style>
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: "#FFFFFF",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "20px",
          paddingBottom: "100px", // Espacio para el tabbar
          "&::-webkit-scrollbar": {
            display: "none",
          },
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        {/* Header con título centrado y notificación */}
        <Box
          sx={{
            width: "100%",
            maxWidth: "800px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginBottom: "40px",
            marginTop: "20px",
            position: "relative",
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: "bold",
              color: "#000",
              fontSize: { xs: "20px", md: "24px" },
              textAlign: "center",
            }}
          >
            Disponibilidad
          </Typography>
          <IconButton
            onClick={handleNotificationsClick}
            sx={{
              color: "#666",
              position: "absolute",
              right: 0,
            }}
          >
            <NotificationsOutlined />
          </IconButton>
        </Box>

        {/* Tarjeta principal */}
        <Card
          sx={{
            width: "100%",
            maxWidth: "800px",
            backgroundColor: "#fff",
            borderRadius: "12px",
            boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
            padding: "24px",
          }}
        >
          {/* Sección superior - Ubicación y Duración */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "24px",
              paddingBottom: "16px",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            {/* Ubicación */}
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: "bold",
                  color: "#000",
                  marginBottom: "8px",
                  fontSize: "14px",
                }}
              >
                Ubicación
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                {imageLoading ? (
                  <SkeletonLoader
                    variant="rectangular"
                    width="20px"
                    height="20px"
                    sx={{
                      borderRadius: "4px",
                    }}
                  />
                ) : (
                  <Box
                    component="img"
                    src="https://iili.io/KcVr9zx.webp"
                    alt="Google Meets"
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                    sx={{
                      width: "20px",
                      height: "20px",
                      borderRadius: "4px",
                    }}
                  />
                )}
                <Typography variant="body2" sx={{ color: "#666", fontSize: "12px" }}>
                  Google Meet
                </Typography>
              </Box>
            </Box>

            {/* Duración */}
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: "bold",
                  color: "#000",
                  marginBottom: "8px",
                  fontSize: "14px",
                }}
              >
                Duración
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <Schedule sx={{ fontSize: "16px", color: "#666" }} />
                <Typography variant="body2" sx={{ color: "#666", fontSize: "12px" }}>
                  50 min
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Sección de Horario */}
          <Box sx={{ marginBottom: "24px" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: "bold",
                  color: "#000",
                  fontSize: "14px",
                }}
              >
                Horario: <span style={{ color: "#1F85F6" }}>personalizado</span>
              </Typography>
              <button
                onClick={(e) => {
                  handleOpenBlockModal();
                  // Efecto visual de click
                  e.target.style.backgroundColor = "#1A6BC7";
                  e.target.style.color = "#FFFFFF";
                  setTimeout(() => {
                    e.target.style.backgroundColor = "#EBF4FF";
                    e.target.style.color = "#1F85F6";
                  }, 150);
                }}
                style={{
                  backgroundColor: "#EBF4FF",
                  color: "#1F85F6",
                  borderRadius: "40px",
                  textTransform: "none",
                  padding: "4px 10px",
                  fontSize: "11px",
                  minWidth: "auto",
                  border: "none",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  outline: "none",
                  transition: "all 0.2s ease",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                + Bloquear horario
              </button>
            </Box>

            {/* Horas semanales */}
            <Box sx={{ marginBottom: "16px" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                <Loop sx={{ fontSize: "16px", color: "#000" }} />
                <Typography variant="body2" sx={{ color: "#000", fontSize: "12px" }}>
                  Horas semanales
                </Typography>

              </Box>
              <Typography variant="body2" sx={{ color: "#666", marginLeft: "24px", fontSize: "11px" }}>
                Establecer cuando usted esté disponible para reuniones
              </Typography>
            </Box>

            {/* Lista de slots de tiempo */}
            <Box sx={{ marginLeft: "28px" }}>
              {timeSlots.map((slot, index) => {
                // Verificar si este slot está bloqueado comparando con los bloqueos
                let isBlocked = slot.isBlocked || false;
                
                if (!isBlocked && slot.startTime && slot.endTime) {
                  const slotStartStr = dateToTimeString(slot.startTime);
                  const slotEndStr = dateToTimeString(slot.endTime);
                  const diaSemana = dayLetterToNumber(slot.day);
                  
                  // Verificar si hay bloqueos que coincidan con este slot
                  const bloqueosDelDia = blockedSchedules.filter(b => b.diaSemana === diaSemana);
                  
                  // Verificar si alguna hora bloqueada está dentro del rango del slot
                  isBlocked = bloqueosDelDia.some(bloqueo => {
                    const [hBloqueo, mBloqueo] = bloqueo.hora.split(':').map(Number);
                    const [hInicio, mInicio] = slotStartStr.split(':').map(Number);
                    const [hFin, mFin] = slotEndStr.split(':').map(Number);
                    
                    const minutosBloqueo = hBloqueo * 60 + mBloqueo;
                    const minutosInicio = hInicio * 60 + mInicio;
                    const minutosFin = hFin * 60 + mFin;
                    
                    // El bloqueo está dentro del rango del slot
                    return minutosBloqueo >= minutosInicio && minutosBloqueo < minutosFin;
                  });
                }
                
                return (
                <Box
                  key={slot.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "8px",
                  }}
                >
                  {/* Solo mostrar el círculo del día si no es un horario adicional */}
                  {!slot.isAdditional && (
                    <Box
                      sx={{
                        width: "32px",
                        height: "32px",
                        backgroundColor: "#1F85F6",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#fff",
                        fontSize: "14px",
                        fontWeight: "bold",
                        minWidth: "32px",
                        minHeight: "32px",
                      }}
                    >
                      {slot.day}
                    </Box>
                  )}
                  <Box sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "2px",
                    marginLeft: slot.isAdditional ? "40px" : "0px" // Alinear con el inicio de los campos de tiempo
                  }}>
                    <TextField
                      ref={(el) => {
                        if (el) {
                          timeInputRefs.current[`start-${slot.id}`] = el.querySelector('input');
                        }
                      }}
                      value={getTimeInputValue(slot.id, 'startTime')}
                      onChange={(e) => {
                        console.log(`TextField startTime onChange: slot.id=${slot.id}, value=`, e.target.value);
                        handleTimeInputChange(slot.id, 'startTime', e.target.value);
                      }}
                      onInput={(e) => {
                        // Capturar cualquier entrada adicional (pegar, etc.) y aplicar formato
                        const key = `${slot.id}-startTime`;
                        const previousValue = timeInputValues[key] || '';
                        const formattedValue = formatTimeInput(e.target.value, previousValue);
                        if (formattedValue !== e.target.value) {
                          e.target.value = formattedValue;
                          handleTimeInputChange(slot.id, 'startTime', formattedValue);
                        }
                      }}
                      onKeyDown={(e) => handleTimeInputKeyDown(slot.id, 'startTime', e)}
                      placeholder="HH:mm"
                      size="small"
                      error={!isValidTimeSlot(slot)}
                      sx={{
                        width: "85px",
                        minWidth: "85px",
                        '& .MuiOutlinedInput-root': {
                          height: "32px",
                          fontSize: "12px",
                          '& fieldset': {
                            borderColor: !isValidTimeSlot(slot) ? '#f44336' : '#e0e0e0',
                          },
                          '&:hover fieldset': {
                            borderColor: !isValidTimeSlot(slot) ? '#f44336' : '#1F85F6',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: !isValidTimeSlot(slot) ? '#f44336' : '#1F85F6',
                          },
                        },
                        '& .MuiInputBase-input': {
                          padding: '6px 8px',
                          fontSize: '12px',
                          textAlign: 'center',
                        }
                      }}
                      inputProps={{
                        maxLength: 5,
                        pattern: '[0-2][0-9]:[0-5][0-9]',
                        title: 'Formato: HH:mm (ej: 09:30)'
                      }}
                    />
                    <Typography variant="body2" sx={{ color: "#000", fontSize: "12px" }}>
                      -
                    </Typography>
                    <TextField
                      ref={(el) => {
                        if (el) {
                          timeInputRefs.current[`end-${slot.id}`] = el.querySelector('input');
                        }
                      }}
                      value={getTimeInputValue(slot.id, 'endTime')}
                      onChange={(e) => {
                        console.log(`TextField endTime onChange: slot.id=${slot.id}, value=`, e.target.value);
                        handleTimeInputChange(slot.id, 'endTime', e.target.value);
                      }}
                      onInput={(e) => {
                        // Capturar cualquier entrada adicional (pegar, etc.) y aplicar formato
                        const key = `${slot.id}-endTime`;
                        const previousValue = timeInputValues[key] || '';
                        const formattedValue = formatTimeInput(e.target.value, previousValue);
                        if (formattedValue !== e.target.value) {
                          e.target.value = formattedValue;
                          handleTimeInputChange(slot.id, 'endTime', formattedValue);
                        }
                      }}
                      onKeyDown={(e) => handleTimeInputKeyDown(slot.id, 'endTime', e)}
                      placeholder="HH:mm"
                      size="small"
                      error={!isValidTimeSlot(slot)}
                      sx={{
                        width: "85px",
                        minWidth: "85px",
                        '& .MuiOutlinedInput-root': {
                          height: "32px",
                          fontSize: "12px",
                          '& fieldset': {
                            borderColor: !isValidTimeSlot(slot) ? '#f44336' : '#e0e0e0',
                          },
                          '&:hover fieldset': {
                            borderColor: !isValidTimeSlot(slot) ? '#f44336' : '#1F85F6',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: !isValidTimeSlot(slot) ? '#f44336' : '#1F85F6',
                          },
                        },
                        '& .MuiInputBase-input': {
                          padding: '6px 8px',
                          fontSize: '12px',
                          textAlign: 'center',
                        }
                      }}
                      inputProps={{
                        maxLength: 5,
                        pattern: '[0-2][0-9]:[0-5][0-9]',
                        title: 'Formato: HH:mm (ej: 17:30)'
                      }}
                    />
                  </Box>
                  {/* Contenedor para botones con alineación específica según el tipo de slot */}
                  <Box sx={{
                    display: "flex",
                    gap: "2px",
                    width: slot.isAdditional ? "24px" : (shouldShowRemoveButton(slot) ? "50px" : "24px"),
                    justifyContent: "flex-start",
                    alignItems: "center",
                    marginLeft: "0px" // Todos los botones en la misma posición
                  }}>
                    {/* Botón + - para slots principales */}
                    {!slot.isAdditional && shouldShowAddButton(slot) && (
                      <Box sx={{ width: "24px", height: "24px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <IconButton
                          size="small"
                          onClick={() => handleAddTimeSlot(slot.day, slot.id)}
                          sx={{ color: "#666", padding: 0 }}
                        >
                          <Add sx={{ fontSize: "16px" }} />
                        </IconButton>
                      </Box>
                    )}

                    {/* Botón de limpiar/eliminar - para slots adicionales y principales con valores */}
                    {shouldShowRemoveButton(slot) && (
                      <Box sx={{
                        width: "24px",
                        height: "24px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                      }}>
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveTimeSlot(slot.id)}
                          sx={{ color: "#666", padding: 0 }}
                        >
                          {slot.isAdditional ? (
                            <Close sx={{ fontSize: "16px" }} />
                          ) : (
                            <Refresh sx={{ fontSize: "16px" }} />
                          )}
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                </Box>
                );
              })}
            </Box>
          </Box>

          {/* Sección de horas según el día */}
          <Box sx={{ marginTop: "32px" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                  <CalendarToday sx={{ fontSize: "16px", color: "#000" }} />
                  <Typography variant="body2" sx={{ color: "#000", fontSize: "12px" }}>
                    Horas según el día
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: "#666", marginLeft: "24px", fontSize: "11px" }}>
                  Ajustar horas para días específicos
                </Typography>
              </Box>
              <button
                onClick={(e) => {
                  handleAddDailyHours();
                  // Efecto visual de click
                  e.target.style.backgroundColor = "#1A6BC7";
                  e.target.style.color = "#FFFFFF";
                  setTimeout(() => {
                    e.target.style.backgroundColor = "#EBF4FF";
                    e.target.style.color = "#1F85F6";
                  }, 150);
                }}
                style={{
                  backgroundColor: "#EBF4FF",
                  color: "#1F85F6",
                  borderRadius: "40px",
                  textTransform: "none",
                  padding: "4px 10px",
                  fontSize: "11px",
                  minWidth: "auto",
                  border: "none",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  outline: "none",
                  transition: "all 0.2s ease",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                + Añadir
              </button>
            </Box>

            {/* Lista de fechas específicas */}
            {specificDates.length > 0 && (
              <Box sx={{ marginTop: "16px", marginLeft: "24px" }}>
                {specificDates.map((specificDate) => (
                  <Box
                    key={specificDate.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      marginBottom: "8px",
                      padding: "8px",
                      backgroundColor: "#F8F9FA",
                      borderRadius: "8px",
                      border: "1px solid #E9ECEF"
                    }}
                  >
                    <CalendarToday sx={{ fontSize: "16px", color: "#1F85F6" }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ color: "#000", fontSize: "12px", fontWeight: "bold" }}>
                        {specificDate.dateString}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#666", fontSize: "11px" }}>
                        {specificDate.startTime.toTimeString().slice(0, 5)} - {specificDate.endTime.toTimeString().slice(0, 5)}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveSpecificDate(specificDate.id)}
                      sx={{ color: "#666", padding: "4px" }}
                    >
                      <Delete sx={{ fontSize: "16px" }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          {/* Sección de horarios bloqueados */}
          <Box sx={{ marginTop: "32px" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                  <Close sx={{ fontSize: "16px", color: blockedSchedules.length > 0 ? "#f44336" : "#666" }} />
                  <Typography variant="body2" sx={{ color: "#000", fontSize: "12px" }}>
                    Horarios bloqueados
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: "#666", marginLeft: "24px", fontSize: "11px" }}>
                  Horarios deshabilitados por día de la semana
                </Typography>
              </Box>
            </Box>

            {/* Lista de bloqueos */}
            <Box sx={{ marginLeft: "24px" }}>
              {blockedSchedules && blockedSchedules.length > 0 ? (
                (() => {
                  // Agrupar bloqueos por día de la semana
                  const diasSemana = { 1: "Lunes", 2: "Martes", 3: "Miércoles", 4: "Jueves", 5: "Viernes", 6: "Sábado", 7: "Domingo" };
                  const bloqueosPorDia = {};
                  
                  blockedSchedules.forEach(bloqueo => {
                    const diaNombre = diasSemana[bloqueo.diaSemana];
                    if (diaNombre) {
                      if (!bloqueosPorDia[diaNombre]) {
                        bloqueosPorDia[diaNombre] = [];
                      }
                      bloqueosPorDia[diaNombre].push(bloqueo);
                    }
                  });

                  return Object.keys(bloqueosPorDia).length > 0 ? (
                    Object.keys(bloqueosPorDia).map(diaNombre => {
                      const bloqueosDelDia = bloqueosPorDia[diaNombre];
                      // Ordenar por hora
                      bloqueosDelDia.sort((a, b) => {
                        const [hA, mA] = a.hora.split(':').map(Number);
                        const [hB, mB] = b.hora.split(':').map(Number);
                        return (hA * 60 + mA) - (hB * 60 + mB);
                      });

                      
                      return (
                        <Box key={diaNombre} sx={{ marginBottom: "12px" }}>
                          <Typography variant="body2" sx={{ color: "#000", fontSize: "12px", fontWeight: "bold", marginBottom: "8px" }}>
                            {diaNombre}
                          </Typography>
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                            {bloqueosDelDia.map(bloqueo => (
                              <Box
                                key={bloqueo.id}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  padding: "6px 12px",
                                  backgroundColor: "#FFEBEE",
                                  borderRadius: "8px",
                                  border: "1px solid #FFCDD2"
                                }}
                              >
                                <Typography variant="body2" sx={{ color: "#D32F2F", fontSize: "12px", fontWeight: "500" }}>
                                  {bloqueo.hora}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={() => deleteBloqueo(bloqueo.id)}
                                  sx={{ 
                                    color: "#D32F2F", 
                                    padding: "2px",
                                    '&:hover': {
                                      backgroundColor: "#FFCDD2"
                                    }
                                  }}
                                >
                                  <Close sx={{ fontSize: "14px" }} />
                                </IconButton>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      );
                    })
                  ) : (
                    <Typography variant="body2" sx={{ color: "#666", fontSize: "12px" }}>
                      No hay horarios bloqueados
                    </Typography>
                  );
                })()
              ) : (
                <Typography variant="body2" sx={{ color: "#666", fontSize: "12px" }}>
                  No hay horarios bloqueados
                </Typography>
              )}
            </Box>
          </Box>

          {/* Indicador de guardado automático */}
          {isSaving && (
            <Box sx={{ marginTop: "24px", display: "flex", justifyContent: "center", alignItems: "center", gap: "8px" }}>
              <LoadingSpinner size="small" />
              <Typography variant="body2" sx={{ color: "#666", fontSize: "12px" }}>
                Guardando cambios...
              </Typography>
            </Box>
          )}
        </Card>
      </Box>

      {/* Snackbar para mensajes de éxito y error */}
      <Snackbar
        open={showSuccessMessage}
        autoHideDuration={4000}
        onClose={() => setShowSuccessMessage(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowSuccessMessage(false)}
          severity="success"
          sx={{ width: '100%' }}
        >
          {successMessage || 'Disponibilidad guardada exitosamente'}
        </Alert>
      </Snackbar>

      <Snackbar
        open={showErrorMessage}
        autoHideDuration={4000}
        onClose={() => setShowErrorMessage(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowErrorMessage(false)}
          severity="error"
          sx={{ width: '100%' }}
        >
          {showErrorMessage && (() => {
            // Determinar el tipo de error basado en si estamos en el modal o en horarios recurrentes

            // Si estamos en el modal de fechas específicas
            if (showDateModal) {
              return getModalErrorMessage();
            }

            // Error para horarios recurrentes: duración mínima
            return "La hora de fin debe ser al menos 1 hora mayor que la hora de inicio.";
          })()}
        </Alert>
      </Snackbar>

      <Snackbar
        open={showOverlapMessage}
        autoHideDuration={4000}
        onClose={() => setShowOverlapMessage(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowOverlapMessage(false)}
          severity="warning"
          sx={{ width: '100%' }}
        >
          Este horario se solapa con otro horario del mismo día. Por favor revise los tiempos.
        </Alert>
      </Snackbar>

      <Popover
        open={Boolean(notificationsAnchorEl)}
        anchorEl={notificationsAnchorEl}
        onClose={handleNotificationsClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          zIndex: 9999,
          '& .MuiPopover-paper': {
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            border: '1px solid #e0e0e0',
            backgroundColor: '#ffffff',
            minWidth: '200px',
            zIndex: 9999,
          }
        }}
      >
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: '#666', fontSize: '14px' }}>
            No hay notificaciones pendientes
          </Typography>
        </Box>
      </Popover>

      {/* Modal para agregar fecha(s) específica(s) */}
      <Dialog
        open={showDateModal}
        onClose={handleCloseDateModal}
        maxWidth="sm"
        fullWidth
        sx={{
          zIndex: 1300,
          '& .MuiDialog-paper': {
            borderRadius: '12px',
            padding: { xs: '16px', sm: '20px' },
            zIndex: 1300,
            position: 'relative',
            maxHeight: '90vh',
            maxWidth: { xs: '90%', sm: '380px' },
            width: { xs: '90%', sm: '380px' },
            margin: 'auto',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          },
          '& .MuiDialogContent-root': {
            overflow: 'auto',
            padding: '0',
            margin: '0',
            '&::-webkit-scrollbar': {
              display: 'none'
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          },
          '& .MuiBackdrop-root': {
            zIndex: 1299,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          }
        }}
      >
        <DialogTitle sx={{
          fontSize: '16px',
          fontWeight: 'bold',
          color: '#000',
          paddingBottom: '12px',
          paddingTop: '0',
          borderBottom: '1px solid #e0e0e0',
          flexShrink: 0,
          textAlign: 'center'
        }}>
          Agregar fecha específica
        </DialogTitle>

        <DialogContent sx={{
          paddingTop: '40px',
          paddingBottom: '0',
          flex: 1,
          overflow: 'auto',
          maxHeight: 'calc(90vh - 140px)',
          '&::-webkit-scrollbar': {
            display: 'none'
          },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }}>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            height: '100%',
            overflow: 'hidden'
          }}>
            {/* Calendario de selección múltiple */}
            <Box>
              <Typography variant="body2" sx={{ color: '#000', fontSize: '13px', fontWeight: 'bold', marginTop: '16px', marginBottom: '10px' }}>
                Seleccione la fecha a la que desea asignar horas específicas
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '8px' }}>
                <IconButton size="small" onClick={goPrevMonth} sx={{ color: '#1F85F6' }}><ChevronLeft /></IconButton>
                <Typography variant="body2" sx={{ color: '#000', fontSize: '13px' }}>
                  {`${new Date(calendarYear, calendarMonth, 1).toLocaleDateString('es-ES', { month: 'long' }).charAt(0).toUpperCase() + new Date(calendarYear, calendarMonth, 1).toLocaleDateString('es-ES', { month: 'long' }).slice(1)} de ${calendarYear}`}
                </Typography>
                <IconButton size="small" onClick={goNextMonth} sx={{ color: '#1F85F6' }}><ChevronRight /></IconButton>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '6px' }}>
                {["LUN", "MAR", "MIÉ", "JUE", "VIE", "SÁB", "DOM"].map((d) => (
                  <Typography key={d} variant="caption" sx={{ textAlign: 'center', color: '#666', fontSize: '11px' }}>{d}</Typography>
                ))}
                {getMonthMatrix(calendarYear, calendarMonth).flat().map((date, idx) => {
                  if (!date) return <Box key={`e-${idx}`} sx={{ height: '36px' }} />;
                  const key = formatDateKey(date);
                  const selected = selectedDateKey === key;
                  // Crear fecha de hoy sin problemas de zona horaria y excluir hoy también
                  const today = new Date();
                  const todayDateOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                  const isPastOrToday = dateOnly <= todayDateOnly;
                  return (
                    <Box key={key}
                      onClick={() => !isPastOrToday && toggleSelectDate(date)}
                      sx={{
                        height: '36px',
                        borderRadius: '18px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: isPastOrToday ? 'not-allowed' : 'pointer',
                        backgroundColor: selected ? '#1F85F6' : '#F1F5F9',
                        color: selected ? '#fff' : (isPastOrToday ? '#bbb' : '#000'),
                        opacity: isPastOrToday ? 0.6 : 1,
                        userSelect: 'none'
                      }}>
                      {date.getDate()}
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {/* Rangos de horas */}
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              marginTop: '18px',
              gap: '8px'
            }}>
              <Typography variant="body2" sx={{ color: '#000', fontSize: '13px', fontWeight: 'bold' }}>
                ¿Qué horas estás disponible?
              </Typography>
              {modalTimeRanges.map((range, idx) => (
                <Box key={range.id} sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <TextField
                    value={range.startText}
                    onChange={(e) => {
                      const v = formatTimeInput(e.target.value, range.startText);
                      setModalTimeRanges(prev => prev.map(r => r.id === range.id ? { ...r, startText: v } : r));
                    }}
                    placeholder="HH:mm"
                    sx={{ width: '100%', maxWidth: '120px', '& .MuiOutlinedInput-root': { height: '36px', fontSize: '13px' } }}
                    inputProps={{ maxLength: 5, pattern: '[0-2][0-9]:[0-5][0-9]', title: 'Formato: HH:mm' }}
                    error={range.startText && !validateTimeFormat(range.startText)}
                  />
                  <Typography variant="body2" sx={{ color: '#000' }}>-</Typography>
                  <TextField
                    value={range.endText}
                    onChange={(e) => {
                      const v = formatTimeInput(e.target.value, range.endText);
                      setModalTimeRanges(prev => prev.map(r => r.id === range.id ? { ...r, endText: v } : r));
                    }}
                    placeholder="HH:mm"
                    sx={{ width: '100%', maxWidth: '120px', '& .MuiOutlinedInput-root': { height: '36px', fontSize: '13px' } }}
                    inputProps={{ maxLength: 5, pattern: '[0-2][0-9]:[0-5][0-9]', title: 'Formato: HH:mm' }}
                    error={range.endText && !validateTimeFormat(range.endText)}
                  />
                  {idx > 0 && (
                    <IconButton size="small" onClick={() => setModalTimeRanges(prev => prev.filter(r => r.id !== range.id))} sx={{ color: '#666' }}>
                      <Close sx={{ fontSize: '16px' }} />
                    </IconButton>
                  )}
                </Box>
              ))}
              <Box sx={{ marginBottom: '20px' }}>
                <button
                  onClick={() => setModalTimeRanges(prev => [...prev, { id: Date.now(), startText: '', endText: '' }])}
                  style={{
                    backgroundColor: '#EBF4FF', color: '#1F85F6', borderRadius: '20px', padding: '4px 10px', fontSize: '11px', border: 'none', cursor: 'pointer', fontFamily: 'inherit'
                  }}
                >
                  + Añadir
                </button>
              </Box>

              {/* Mensaje de error de solapamiento */}
              {modalOverlapError && (
                <Box sx={{
                  backgroundColor: '#FFEBEE',
                  border: '1px solid #FFCDD2',
                  borderRadius: '8px',
                  padding: '8px 12px',
                  marginTop: '8px'
                }}>
                  <Typography variant="body2" sx={{
                    color: '#D32F2F',
                    fontSize: '12px',
                    fontWeight: '500',
                    textAlign: 'center'
                  }}>
                    Las horas seleccionadas se solapan.
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{
          paddingTop: '20px',
          paddingBottom: '0',
          gap: '12px',
          flexShrink: 0,
          justifyContent: 'center'
        }}>
          <ArgonButton
            onClick={handleOpenConfirmSaveBulk}
            variant="contained"
            color="primary"
            disabled={!canSaveBulk()}
            sx={{
              backgroundColor: '#1F85F6',
              color: '#fff',
              textTransform: 'none',
              fontSize: '14px',
              borderRadius: '8px',
              padding: '8px 24px',
              '&:hover': {
                backgroundColor: '#1976D2',
              },
              '&:disabled': {
                backgroundColor: '#e0e0e0',
                color: '#999'
              }
            }}
          >
            Guardar
          </ArgonButton>
          <ArgonButton
            onClick={handleCloseDateModal}
            variant="text"
            color="dark"
            sx={{
              color: '#666',
              textTransform: 'none',
              fontSize: '14px'
            }}
          >
            Cancelar
          </ArgonButton>
        </DialogActions>
      </Dialog>
      <Dialog
        open={showConfirmSaveBulk}
        onClose={handleCloseConfirmSaveBulk}
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '12px'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '18px' }}>
          Confirmar guardado
        </DialogTitle>
        <DialogContent sx={{ fontSize: '14px' }}>
          ¿Deseas guardar las horas seleccionadas para la fecha elegida?
        </DialogContent>
        <DialogActions sx={{ padding: '16px' }}>
          <ArgonButton
            onClick={handleConfirmSaveBulk}
            variant="contained"
            color="primary"
            sx={{ textTransform: 'none' }}
          >
            Confirmar
          </ArgonButton>
          <ArgonButton
            onClick={handleCloseConfirmSaveBulk}
            variant="text"
            color="dark"
            sx={{ textTransform: 'none' }}
          >
            Cancelar
          </ArgonButton>

        </DialogActions>
      </Dialog>

      {/* Modal para bloquear horario */}
      <Dialog
        open={showBlockModal}
        onClose={handleCloseBlockModal}
        maxWidth="sm"
        fullWidth
        sx={{
          zIndex: 1300,
          '& .MuiDialog-paper': {
            borderRadius: '12px',
            padding: { xs: '16px', sm: '20px' },
            zIndex: 1300,
            position: 'relative',
            maxHeight: '90vh',
            maxWidth: { xs: '90%', sm: '380px' },
            width: { xs: '90%', sm: '380px' },
            margin: 'auto',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          },
          '& .MuiDialogContent-root': {
            overflow: 'auto',
            padding: '0',
            margin: '0',
            '&::-webkit-scrollbar': {
              display: 'none'
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          },
          '& .MuiBackdrop-root': {
            zIndex: 1299,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          }
        }}
      >
        <DialogTitle sx={{
          fontSize: '16px',
          fontWeight: 'bold',
          color: '#000',
          paddingBottom: '12px',
          paddingTop: '0',
          borderBottom: '1px solid #e0e0e0',
          flexShrink: 0,
          textAlign: 'center'
        }}>
          Bloquear horario
        </DialogTitle>

        <DialogContent sx={{
          paddingTop: '40px',
          paddingBottom: '0',
          flex: 1,
          overflow: 'auto',
          maxHeight: 'calc(90vh - 140px)',
          '&::-webkit-scrollbar': {
            display: 'none'
          },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }}>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            height: '100%',
            overflow: 'hidden'
          }}>
            {/* Selección de día de la semana */}
            <Box>
              <Typography variant="body2" sx={{ color: '#000', fontSize: '13px', fontWeight: 'bold', marginTop: '16px', marginBottom: '10px' }}>
                Seleccione el día de la semana
              </Typography>
              <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
                {[
                  { letter: 'L', name: 'Lunes' },
                  { letter: 'M', name: 'Martes' },
                  { letter: 'Mi', name: 'Miércoles' },
                  { letter: 'J', name: 'Jueves' },
                  { letter: 'V', name: 'Viernes' },
                  { letter: 'S', name: 'Sábado' },
                  { letter: 'D', name: 'Domingo' }
                ].map((day) => {
                  const selected = blockSelectedDay === day.letter;
                  return (
                    <Box
                      key={day.letter}
                      onClick={() => toggleSelectDayBlock(day.letter)}
                      sx={{
                        minWidth: '80px',
                        height: '40px',
                        borderRadius: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        backgroundColor: selected ? '#1F85F6' : '#F1F5F9',
                        color: selected ? '#fff' : '#000',
                        userSelect: 'none',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: selected ? '#1976D2' : '#E3F2FD',
                        }
                      }}
                    >
                      <Typography variant="body2" sx={{ fontSize: '12px', fontWeight: selected ? 'bold' : 'normal' }}>
                        {day.name}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {/* Campos de hora */}
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              marginTop: '18px',
              gap: '8px'
            }}>
              <Typography variant="body2" sx={{ color: '#000', fontSize: '13px', fontWeight: 'bold' }}>
                Horario a bloquear
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '8px', flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 120px', minWidth: '100px', maxWidth: '150px' }}>
                  <Typography
                    component="label"
                    htmlFor="block-horario-inicio"
                    variant="caption"
                    sx={{ display: 'block', color: '#64748b', fontSize: '12px', fontWeight: 600, marginBottom: '6px' }}
                  >
                    Hora inicio
                  </Typography>
                  <TextField
                    id="block-horario-inicio"
                    value={blockStartTime}
                    onChange={(e) => {
                      const v = formatTimeInput(e.target.value, blockStartTime);
                      setBlockStartTime(v);
                    }}
                    placeholder="HH:mm"
                    fullWidth
                    size="small"
                    variant="outlined"
                    sx={{ '& .MuiOutlinedInput-root': { fontSize: '14px' } }}
                    inputProps={{ maxLength: 5, pattern: '[0-2][0-9]:[0-5][0-9]', title: 'Formato: HH:mm' }}
                    error={blockStartTime && !validateTimeFormat(blockStartTime)}
                  />
                </Box>
                <Typography variant="body2" sx={{ color: '#64748b', paddingBottom: '8px', flexShrink: 0 }}>
                  —
                </Typography>
                <Box sx={{ flex: '1 1 120px', minWidth: '100px', maxWidth: '150px' }}>
                  <Typography
                    component="label"
                    htmlFor="block-horario-fin"
                    variant="caption"
                    sx={{ display: 'block', color: '#64748b', fontSize: '12px', fontWeight: 600, marginBottom: '6px' }}
                  >
                    Hora fin
                  </Typography>
                  <TextField
                    id="block-horario-fin"
                    value={blockEndTime}
                    onChange={(e) => {
                      const v = formatTimeInput(e.target.value, blockEndTime);
                      setBlockEndTime(v);
                    }}
                    placeholder="HH:mm"
                    fullWidth
                    size="small"
                    variant="outlined"
                    sx={{ '& .MuiOutlinedInput-root': { fontSize: '14px' } }}
                    inputProps={{ maxLength: 5, pattern: '[0-2][0-9]:[0-5][0-9]', title: 'Formato: HH:mm' }}
                    error={blockEndTime && !validateTimeFormat(blockEndTime)}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{
          paddingTop: '20px',
          paddingBottom: '0',
          gap: '12px',
          flexShrink: 0,
          justifyContent: 'center'
        }}>
          <ArgonButton
            onClick={blockSchedule}
            variant="contained"
            color="primary"
            disabled={!blockSelectedDay || !blockStartTime || !blockEndTime || !validateTimeFormat(blockStartTime) || !validateTimeFormat(blockEndTime) || isBlocking}
            sx={{
              backgroundColor: '#1F85F6',
              color: '#fff',
              textTransform: 'none',
              fontSize: '14px',
              borderRadius: '8px',
              padding: '8px 24px',
              '&:hover': {
                backgroundColor: '#1976D2',
              },
              '&:disabled': {
                backgroundColor: '#e0e0e0',
                color: '#999'
              }
            }}
          >
            {isBlocking ? 'Bloqueando...' : 'Bloquear'}
          </ArgonButton>
          <ArgonButton
            onClick={handleCloseBlockModal}
            variant="text"
            color="dark"
            disabled={isBlocking}
            sx={{
              color: '#666',
              textTransform: 'none',
              fontSize: '14px'
            }}
          >
            Cancelar
          </ArgonButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Horarios;
